import { useState, useContext, createContext, useEffect, useRef, useMemo } from "react";

// APIs
import api, { TRANSCRIPT_CHUNK_THRESHOLD } from "../services/api";

// Context
import { useAuth } from "./useAuth";
import { useFeatures } from "./useFeatures";
import { useNoteType } from "./useNoteType";
import { getNoteTypeFeature } from "./useFeatures";

// Libs
import { subscribe, unsubscribe } from "../utils/events";
import { toast } from "react-toastify";
import { withToastRailMetadata } from "../utils/toastRailMetadata";
import moment from "moment";
import { useJob } from "./useJobs";
import { useDebouncedCallback } from "use-debounce";
import { billingBlockText } from "../utils/billingBlock";
import { audit, hasOwn, sendToMobileApp, normalizList, normalizeAssignedToForNative } from "../utils/utils";
import { encodeAudioBufferToMp3 } from "../utils/audioEncoding";
import { publish } from "../utils/events";
import { diffSectionEdit } from "../utils/sectionEditDiff";
import { getHighestReassessmentIndex, DISPOSITION_PHASES, DISPO_VARIABLE_TO_LABEL } from "../utils/erPhases";
import { useTranslation } from "react-i18next";
import { getNoteTemplateForChat, hasNewTemplateForChat } from "../utils/noteTemplateUtils";
import { buildModeSettings, assignedUserId, pickSettingsSource } from "../utils/generationModeSettings";
import { applyIcd10SexGate } from "../utils/icd10SexGate";
import * as icd10PendingSync from "../utils/icd10PendingSync";
import {
  refetchChatUntilTranscriptAdvances,
  chatTranscriptSignal,
  chatHasAnyTranscript,
  chatHasMeaningfulTranscript,
  noteRelevantTranscriptSignal,
} from "../utils/transcriptRefresh";
import useTranscriptionWatchdog, { LONG_RECORDING_CEILING_MS } from "./useTranscriptionWatchdog";
import { buildIcd10RemapMap, remapApplies, applyIcd10Remap } from "../utils/icd10Library";
import { buildChatSearchFilters } from "../utils/chatSearch";
import { parseTranscript } from "../utils/transcriptParser";
import { logRecordingSaveFailure } from "../utils/recordingSaveError";
import {
  buildChatDataIdentityKey,
  buildChatListRequestFilters,
  buildChatListScopeKey,
  clearChatListGroup,
  DEFAULT_CHAT_LIST_FILTERS,
  getLastServerScopedChat,
  normalizeChatListFilters,
  shouldApplyChatDataResponse,
  shouldResetChatListIdentity,
} from "../utils/chatListFilters";
import { logActivity } from "../utils/activityLog";
import { dismissRecordingProgressToast, recordingProgressToastId, showRecordingProgressToast } from "../utils/recordingToast";
import { loadChatList, buildScope, isCacheFresh } from "../utils/offlineCache";
import { isAuthStatus, isServerDownError, statusOf } from "../utils/serverErrors";
import {
  shouldHydrateFromCache,
  shouldPersistChatWindow,
  persistChatWindowWithOwnership,
  statesOwner,
  mergeUnsyncedTempChats,
  TEMP_CHAT_ID_PREFIX,
  offlineIdOf,
} from "./useChatsOfflineHelpers";
import {
  evaluateRecordingIdentity,
  identityTimeMs,
  encounterUuidOf,
  IDENTITY_ERROR_CODE,
  IDENTITY_FETCH_TIMEOUT_MS,
  IDENTITY_FRESH_MS,
  IDENTITY_REFUSED,
} from "../utils/recordingIdentityGuard";
import { reportIdentityParked, reportIdentityOverride } from "../utils/recordingIdentityTelemetry";

// How long a cached chat-list snapshot may stand in for a live ownership answer
// when the server is unreachable. Past this, the snapshot is treated as unknown
// and the offline write is refused rather than decided on day-old ownership.
//
// This bound is now HONEST: persistChatWindowWithOwnership stamps `fetchedAt`
// with the ownership-as-of time rather than the save time, so the guarantee is
// exactly "a server stated this owner within CACHED_OWNERSHIP_MAX_AGE_MS". It
// used to be re-stamped with the save time even when every ownership value came
// out of the 5-minute memo with no server call in that refresh at all, which
// made the real bound 24h + 5min, invisibly.
//
// OPEN FOR THE CLINICAL OWNER: 24h is kept unchanged in this wave because
// tightening it is a clinical-policy call (how long may a device act on a stale
// ownership answer?), not an engineering one -- the alternative to trusting a
// <=24h-old answer is refusing the clinician's offline edit outright.
// Recommendation when they rule: 12h, one shift plus handover.
const CACHED_OWNERSHIP_MAX_AGE_MS = 24 * 60 * 60 * 1000;

export const ChatsContext = createContext();

function useChatsProvider() {
  const { t } = useTranslation();

  const { noteTypesMap, noteTypes } = useNoteType();
  const {
    auth,
    brainId,
    location,
    isDoctor,
    isServerAvailable,
    isMobile,
    isMobileApp,
    isChromeExtension,
    updateProfile,
    isAdminUser,
    columnsSmartSheetKeys,
    checkUserPlan,
    hasGroup,
  } = useAuth();
  const { hasFeature } = useFeatures();
  const { setGenerating, markRegeneratingCompleteIfChanged, markRegeneratingAborted } = useJob();
  const chatDataIdentity = buildChatDataIdentityKey(auth?.user?.id, auth?.session?.currentOrganizationName);
  // Update during render, before effects run, so an old request resolving in
  // the render-to-effect window cannot write the previous user's encounter.
  const activeChatDataIdentityRef = useRef(chatDataIdentity);
  activeChatDataIdentityRef.current = chatDataIdentity;

  // Open-chat resilience for stalled transcriptions. Main-encounter recordings are
  // sent async (api.transcript returns 200 immediately) and the UI waits for the
  // "transcriptionCompleted" SSE to paint + clear the spinner. If that event never
  // arrives (backend failure or a dropped SSE), the spinner spins forever. This
  // watchdog polls getChat to paint a transcript that actually landed, and at a hard
  // ceiling clears the spinner with an honest-fail toast instead of hanging. It is
  // strictly read-only — it NEVER re-transcribes (that path deletes the audio backup
  // before a transcript is confirmed; see the header of useTranscriptionWatchdog.js).
  const transcriptionWatchdog = useTranscriptionWatchdog({
    getChat: (id) => api.getChat(id),
    onLanded: (landedChat, { dataIdentity }) => {
      if (shouldApplyChatDataResponse(dataIdentity, activeChatDataIdentityRef.current)) updateChatLocal(landedChat);
    },
    onStalled: ({ chatId, recordingId, reload, dataIdentity }) => {
      // The native progress capsule is keyed by recordingId and is sticky by
      // design, so declaring an honest failure has to retract it too — otherwise
      // the phone shows a spinning "Preparing your transcription" directly above
      // the toast that just said transcription didn't finish. The watchdog has
      // always passed recordingId; this consumer used to drop it.
      //
      // Ahead of the identity guard below, deliberately. That guard is about not
      // writing one session's verdict onto another's encounter; this capsule is
      // not data, it is a spinner already on THIS screen for work that has given
      // up. The ceiling fires on a timer, so it routinely lands after a sign-out
      // — and behind the guard, signing out and back in (what a clinician tries
      // when the capsule looks stuck) left it spinning to its own 600s ceiling.
      if (isMobileApp && recordingId) dismissRecordingProgressToast(toast, recordingProgressToastId(recordingId));
      // KNOWN GAP, deliberately left open — do not "finish the job" without reading
      // this. Retracting the capsule above also clears the working-ceiling's
      // hand-back handler, so nothing tells the shell anything and the recording
      // stays parked at "parsing" on the device until an app restart.
      //
      // Releasing it as "error" here looks like the obvious fix and was written,
      // tested, then deliberately pulled: this path arms only AFTER api.transcript
      // acked, so the audio is already on the server. "error" makes the shell
      // re-offer and re-upload audio the backend has, and for a recording whose
      // transcription permanently fails that cycle repeats on the shell's backoff
      // indefinitely. "parsed" is not safe either — the shell may drop audio whose
      // transcript never landed. Picking correctly needs the native app's actual
      // per-state semantics (lucasai-app, not this repo), which is also why this
      // module is documented as strictly read-only and never re-transcribing.
      if (!shouldApplyChatDataResponse(dataIdentity, activeChatDataIdentityRef.current)) return;
      updateChatValue(chatId, { transcribing: false });
      // Clickable toast (closeOnClick is on by default): tapping re-reads the chat
      // and paints if the transcript has since landed. No re-transcription.
      toast.error(t("hooks.useChats.transcriptionStalled"), { autoClose: false, onClick: () => reload() });
    },
  });

  const [chats, setChats] = useState([]);
  // Timestamp of the offline cache row this list was hydrated from (spec §2);
  // null whenever the list reflects a live server fetch. Lets the UI show a
  // "last updated" affordance while offline instead of implying freshness.
  const [chatsCachedAt, setChatsCachedAt] = useState(null);
  const [chat, setChat] = useState();
  const autogenerate = useRef();
  const [autogeneratePendingChatId, setAutogeneratePendingChatId] = useState();
  // chatIds whose persisted ICD-10 pending write we've already re-fired this
  // session, so the crash/close replay effect doesn't double-send.
  const icd10ReplayedRef = useRef(new Set());
  // Tracks chatIds whose generation was kicked off in THIS client session.
  // The stale-recovery clear in refreshChat() must skip these — otherwise an
  // in-flight refreshChat (called by generate() right after api.generate()
  // returns) would see the chat's pre-existing noteDesign and incorrectly
  // flip isGenerating back to false mid-run, hiding the "Generating note"
  // loader. Cleared on generationCompleted SSE / explicit setGenerating(false).
  const activeGenerationsRef = useRef(new Set());

  // Org doctors keyed by id, fetched lazily for assigned-provider mode settings
  // (see generate). 5-minute TTL: profile generation settings change rarely, but a
  // whole session is too long to hold a doctor's stale settings after they edit them.
  const doctorSettingsCacheRef = useRef({ at: 0, byId: null });
  const findDoctorForSettings = async (userId) => {
    const TTL_MS = 5 * 60 * 1000;
    const cache = doctorSettingsCacheRef.current;
    if (!cache.byId || Date.now() - cache.at > TTL_MS) {
      try {
        const res = await api.listDoctors();
        const byId = {};
        for (const u of res?.data?.users || []) byId[u.id] = u;
        doctorSettingsCacheRef.current = { at: Date.now(), byId };
      } catch (e) {
        // Offline or denied: keep whatever we had (or an empty map) so generation
        // falls back to the clicking user's settings instead of failing — but leave
        // the cache expired (at: 0) so the NEXT generation retries, rather than
        // locking a transient failure in for the whole TTL window.
        console.error("listDoctors for assigned-provider mode settings failed:", e);
        doctorSettingsCacheRef.current = { at: 0, byId: cache.byId || {} };
      }
    }
    return doctorSettingsCacheRef.current.byId[userId];
  };
  // temp- ids the native offline replay has told us actually landed on the
  // server. The list merge (mergeUnsyncedTempChats) pins unsynced temp- rows on
  // top of every fetched window so offline work cannot vanish; this is what
  // eventually RETIRES one, as the backstop for the durable-twin join in case
  // the row projection does not carry the offlineId stamp back. A ref, not
  // state: it must never re-render the list on its own, and refreshList reads
  // it at fetch time.
  const syncedTempChatIdsRef = useRef(new Set());
  const [showEditContextVariable, setShowEditContextVariable] = useState();
  const [showRequestPin, setShowRequestPin] = useState(false);
  const [showModalDelete, setShowModalDelete] = useState(false);
  const [showModalSelectEncounterType, setShowModalSelectEncounterType] = useState(false);
  const [showModalCreateFollowUp, setShowModalCreateFollowUp] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedChatIds, setSelectedChatIds] = useState([]);
  const [showNewChat, setShowNewChat] = useState();
  const [filterIncomplete, setFilterIncomplete] = useState(false);
  const [filterSuggestion, setFilterSuggestion] = useState(false);
  const [showReassignChat, setShowReassignChat] = useState();
  const [showReportCard, setShowReportCard] = useState();
  const [followUpOnSign, setFollowUpOnSign] = useState(false);

  // Loading states

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState();
  const [loadableMore, setLoadableMore] = useState();
  const [importingChat, setImportingChat] = useState(false);

  const [filters, setFilters] = useState(() => ({ ...DEFAULT_CHAT_LIST_FILTERS }));
  const [loadedChatListScope, setLoadedChatListScope] = useState(null);
  const [serverScopedChatIds, setServerScopedChatIds] = useState(() => new Set());
  const previousChatDataIdentityRef = useRef(chatDataIdentity);
  // The live search query is kept OUT of `filters` on purpose: `filters` is
  // persisted to the user profile (chatListFilters), and a typed search term
  // must never be saved as a preference or restored stale on next login.
  const [searchTerm, setSearchTerm] = useState("");
  const parsedSearch = useMemo(() => buildChatSearchFilters(searchTerm).parsed, [searchTerm]);

  useEffect(() => {
    if (!shouldResetChatListIdentity(previousChatDataIdentityRef.current, chatDataIdentity)) return;

    previousChatDataIdentityRef.current = chatDataIdentity;
    setChats([]);
    setChat();
    setServerScopedChatIds(new Set());
    setLoadedChatListScope(null);
    setLoadableMore(false);
    setLoadingMore(false);
  }, [chatDataIdentity]);

  // Some legacy/custom encounter types include noteDesign sections without a
  // `.variable` (e.g. raw content blocks like "Summary"). Without a stable id
  // those sections can't participate in the new-flow reorder, exclude-by-
  // variable, or drag-and-drop pipeline — so anywhere they appear, the chat
  // falls back to legacy index-keyed templates and the user's saved order is
  // ignored. Synthesize stable variables once per chat load so every consumer
  // (ChatNoteTab, getNoteTemplate, the Customize Note modal) sees a uniform,
  // variable-keyed sections array.
  const noteDesignSections = chat?.contextVariables?.["local:noteDesign"]?.sections;
  useEffect(() => {
    // Guard against malformed contextVariables where `sections` is present but not an
    // array (object/string from a partially-saved or legacy payload). Optional chaining
    // alone passes a non-array through to `.some`, throwing "some is not a function"
    // (Sentry LUCASHEALTH-R5). Skip synthesis and fall back to legacy rendering instead.
    if (!Array.isArray(noteDesignSections) || !noteDesignSections.some((s) => s.variable === undefined)) return;
    const sections = noteDesignSections.map((s, i) => {
      if (s.variable) return s;
      const text = (s.content || s.title || "").replace(/<[^>]*>/g, "").trim();
      const isSep = text.length === 0 || /^[-=*_\s]+$/.test(text);
      return {
        ...s,
        variable: isSep ? `_separator_${i}` : `_legacy_${i}`,
        isSeparator: s.isSeparator ?? isSep,
      };
    });
    setChat((prev) => {
      if (!prev?.contextVariables?.["local:noteDesign"]) return prev;
      return {
        ...prev,
        contextVariables: {
          ...prev.contextVariables,
          "local:noteDesign": { ...prev.contextVariables["local:noteDesign"], sections },
        },
      };
    });
  }, [noteDesignSections]);

  useEffect(() => {
    if (auth?.user?.id) {
      setFilters(normalizeChatListFilters(auth?.user?.custom?.chatListFilters, { isAdminUser }));
    }

    // eslint-disable-next-line
  }, [auth?.user?.id]);

  const isNoteEnabled = (noteValue, source, options = {}) => {
    let availableNotes = auth?.user?.custom?.availableNotes;
    let doctorType = auth?.user?.custom?.doctorType;

    if (source) {
      if (source.availableNotes || source.doctorType) {
        availableNotes = source.availableNotes ?? availableNotes;
        doctorType = source.doctorType ?? doctorType;
      } else {
        const custom = source.custom ?? source;
        availableNotes = custom?.availableNotes ?? availableNotes;
        doctorType = custom?.doctorType ?? doctorType;
      }
    }

    // check subscription-based feature restrictions
    if (!options.skipFeatureCheck) {
      const requiredFeature = getNoteTypeFeature(noteValue);
      if (requiredFeature && !hasFeature(requiredFeature)) {
        return false;
      }
    }

    if (!doctorType) {
      if (!availableNotes) return true;
      return availableNotes[noteValue] !== false;
    }

    if (availableNotes && hasOwn(availableNotes, noteValue)) {
      return !!availableNotes[noteValue];
    }

    const note = noteTypesMap[noteValue];
    if (!note) return false;
    const noteDoctorTypes = note.doctorTypes;

    if (!noteDoctorTypes || (Array.isArray(noteDoctorTypes) && noteDoctorTypes.length === 0)) return true;
    if (noteDoctorTypes === "*") return true;
    if (Array.isArray(noteDoctorTypes)) {
      return noteDoctorTypes.indexOf(doctorType) !== -1;
    }

    return noteDoctorTypes === doctorType;
  };

  const noteAllowed = (noteValue) => {
    return isNoteEnabled(noteValue);
  };

  useEffect(() => {
    if (!isDoctor) {
      setFilters((currentFilters) => clearChatListGroup(currentFilters));
    }
    // eslint-disable-next-line
  }, [isDoctor]);

  const refreshList = async (abortController) => {
    // `loading` starts true, so bailing out without clearing it pins the
    // encounter-list spinner forever.
    if (!hasFeature("encounters")) {
      setLoading(false);
      return;
    }

    const requestIdentity = chatDataIdentity;
    const requestGroup = filters.group || "";
    const requestScope = buildChatListScopeKey(requestGroup, auth?.user?.id);
    setLoading(true);
    setLoadableMore(true);

    try {
      if (isServerAvailable && auth?.user?.id) {
        if (JSON.stringify(filters) !== JSON.stringify(auth.user.custom?.chatListFilters)) {
          // Refresh user profile to get latest filters
          updateProfile({
            custom: {
              chatListFilters: filters,
            },
          });
        }

        // Refresh chats
        let limit = 100;
        let patients = await api.getChats(
          brainId,
          "",
          "",
          location,
          buildChatListRequestFilters(filters, searchTerm, auth.user.id),
          limit,
          abortController
        );
        if (!shouldApplyChatDataResponse(requestIdentity, activeChatDataIdentityRef.current)) return;
        if (patients.data) {
          if (patients.data.chats.length !== limit) {
            setLoadableMore(false);
          }
          // MERGE, never replace. An encounter created while the server was
          // unreachable is still local-only until the native replay files it,
          // and on reconnect this window does not contain it yet -- so a
          // wholesale replacement made the clinician's just-created encounter
          // disappear from the list, returning only after replay + the next
          // refresh, or never if the replay failed. mergeUnsyncedTempChats
          // pins the unsynced temp- rows on top of the fetched window and
          // retires each one only on proof it landed (durable twin in the
          // window, or a replay ack in syncedTempChatIdsRef).
          setChats(mergeUnsyncedTempChats(chats, patients.data.chats, { syncedTempIds: syncedTempChatIdsRef.current }));
          // Persist the freshly fetched window for offline mode (spec §2), but
          // only when this response IS the unfiltered default window — a
          // search- or group-narrowed fetch would otherwise overwrite the
          // cache with a subset (and a no-match search would erase it).
          // Fire-and-forget: cache failure must never affect the live list.
          //
          // Ownership is hydrated onto the rows first (one bounded
          // getEncounterAssignees lookup for the whole window, not one per row).
          // web/organization-chats omits assignedTo, and the offline assign gate
          // (ensureAssignedToMe) refuses any cached row that does not state an
          // owner -- so without this the cached window can only ever fail closed
          // and the gate's cache fallback is unreachable code.
          //
          // The same abortController the list fetch used is forwarded, so a
          // superseded refresh drops its hydration lookup instead of holding a
          // request nobody is waiting for -- AND skips the save entirely rather
          // than writing rows nobody could hydrate. And .catch is not
          // decoration: the save opens IndexedDB outside its own try, so a
          // rejection here would be an unhandled promise rejection on a
          // fire-and-forget call.
          if (shouldPersistChatWindow({ searchTerm, filters })) {
            persistChatWindowWithOwnership(
              buildScope(auth?.user?.id, auth?.session?.currentOrganizationName),
              patients.data.chats,
              api.getEncounterAssignees,
              { abortController }
            ).catch((e) => console.warn("Offline chat window persist failed:", e?.name || e?.message));
          }
          setChatsCachedAt(null);
          setServerScopedChatIds(new Set(patients.data.chats.map((chat) => chat.id)));
          setLoadedChatListScope(requestScope);
        }
      } else {
        // Server unreachable: hydrate from the offline cache — but never
        // clobber chats already present (native cold-start injection or a
        // previously loaded list). `chats` is read here via closure rather
        // than a ref: refreshList is a brand-new closure every render, and
        // useDebouncedCallback (use-debounce) reassigns its internal
        // function ref synchronously on every render — not inside an effect
        // — so refreshListDebounce always invokes the most recently
        // rendered refreshList, which closes over that render's `chats`.
        // That's as current as `chats` ever gets outside of a functional
        // setState updater, so no separate ref is needed here.
        if (shouldHydrateFromCache({ isServerAvailable, currentChatCount: chats?.length || 0 })) {
          const cached = await loadChatList(buildScope(auth?.user?.id, auth?.session?.currentOrganizationName));
          if (cached) {
            setChats(cached.rows);
            setChatsCachedAt(cached.fetchedAt);
          }
        }
        setLoadableMore(false);
        setLoading(false);
        setServerScopedChatIds(new Set());
        setLoadedChatListScope(requestScope);
      }
    } catch (e) {
      if (!shouldApplyChatDataResponse(requestIdentity, activeChatDataIdentityRef.current)) return;
      if (e?.cause?.code !== "ERR_CANCELED") {
        // Do not leave a group transition permanently loading after a failed
        // request, and do not trust compact rows from the previous scope.
        if (loadedChatListScope !== requestScope) setChats([]);
        setServerScopedChatIds(new Set());
        setLoadedChatListScope(requestScope);
        console.error(e);
        toast.warn(t("hooks.useChats.unableToLoadEncounters"));
      }
    }

    setLoading(false);
  };
  const refreshListDebounce = useDebouncedCallback(refreshList, 700);

  // Server recovered: reload the live list (replaces any cached hydrate).
  useEffect(() => {
    const onServerBack = () => refreshListDebounce();
    subscribe("serverBackOnline", onServerBack);
    return () => unsubscribe("serverBackOnline", onServerBack);
    // eslint-disable-next-line
  }, []);

  // The native offline replay filed (or found already filed) the encounter this
  // temp- id stands for. Remember it so the next server window retires the
  // pinned local row instead of listing the encounter twice. Recorded rather
  // than removed on the spot: dropping the row here, before the durable one has
  // been fetched, is the same flicker-then-vanish this whole merge exists to
  // stop.
  useEffect(() => {
    const onOfflineEncounterSynced = (event) => {
      const tempId = event?.detail?.tempId;
      if (typeof tempId === "string" && tempId.startsWith(TEMP_CHAT_ID_PREFIX)) {
        syncedTempChatIdsRef.current.add(tempId);
      }
    };
    subscribe("offlineEncounterSynced", onOfflineEncounterSynced);
    return () => unsubscribe("offlineEncounterSynced", onOfflineEncounterSynced);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    // Loading is intentionally NOT set here: doing so on every keystroke lights
    // the spinner during the debounce window when no request is in flight.
    // refreshList sets loading the moment the debounced fetch actually fires.
    if (auth) {
      refreshListDebounce(controller);
    }

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line
  }, [auth?.user?.id, location, filters, searchTerm]);

  // Setting Message ID
  useEffect(() => {
    const encounterCreated = async (event) => {
      refreshChat(event.detail.chatId);
    };

    const encounterUpdated = async (event) => {
      refreshChat(event.detail.chatId);
    };

    const generationCompleted = async (event) => {
      // Clear active-session marker so subsequent refreshChat calls can run
      // their stale-noteDesign recovery normally (see refreshChat note).
      activeGenerationsRef.current.delete(event.detail.chatId);
      if (chats.find((c) => c.id === event.detail.chatId)) {
        const updatedChat = await refreshChat(event.detail.chatId);
        // Server-driven (re)generation just landed new codes — apply the org
        // ICD-10 library remap (mirrors the generate() call site). No-op unless
        // the org has a library and a generated code matches.
        await remapIcd10IfNeeded(updatedChat);
        // SSE gives us a positive "something completed" signal. After the
        // refresh, mark the oldest pending regen complete if no content
        // visibly changed — handles regens that produce identical output
        // (common for short sections like Allergies / Past Surgical History
        // that often regenerate to the same "None" / short list). FIFO
        // assumption: the SSE corresponds to the oldest still-pending entry.
        if (updatedChat?.contextVariables) {
          markRegeneratingCompleteIfChanged(event.detail.chatId, updatedChat.contextVariables, {
            markOldestIfNoneChanged: true,
          });
        }
      }

      if (event.detail.requesterUserId === auth?.user?.id)
        toast.info(t("hooks.useChats.generationComplete"), {
          toastId: `generation-complete`,
        });
    };

    // Recovery for a full-note generation whose completion push never arrived.
    // Published by useJobs.setGenerating's timers (45s / 90s soft, 180s hard).
    // Refetch the chat and clear the stuck browser flag once the note is actually
    // done (server no longer generating), or unconditionally at the hard backstop
    // (force) -- so a note can never stay locked indefinitely on a dropped
    // "assessment" SSE. Mirrors the SSE-driven generationCompleted handler above.
    const generationRecoveryCheck = async (event) => {
      const { chatId, force } = event.detail;
      let serverDone = false;
      try {
        const updatedChat = await refreshChat(chatId);
        const cv = updatedChat?.contextVariables;
        serverDone = !!(cv && cv["local:noteDesign"] && !cv["local:generating"]);
      } catch (e) {
        // Refetch failed -- fall through so the hard backstop (force) still clears
        // the stuck flag; a transient network error must not leave a note locked.
      }
      if (force || serverDone) {
        activeGenerationsRef.current.delete(chatId);
        setGenerating(chatId, false);
      }
    };

    const importEncounter = async (event) => {
      console.log("Import encounter", event.detail);
      setImportingChat(false);

      let metadata = event.detail.metadata.data;

      setShowNewChat({
        quickStart: true,
        chat: {
          contextVariables: {
            "local:patientContext": metadata.narrative,
          },
        },
        patient: {
          firstName: metadata.firstName,
          lastName: metadata.lastName,
          dob: metadata.dob,
          mobilePhone: metadata.phone,
          custom: {
            gender: metadata.gender === "male" ? "m" : metadata.gender === "female" ? "f" : null,
          },
        },
      });
    };

    const transcriptionCompleted = async (event) => {
      const requestIdentity = chatDataIdentity;
      // The completion event arrived (success OR error) — the watchdog's job is done
      // either way. Disarm before doing anything else so a pending poll/ceiling can't
      // race the handler.
      if (event.detail.recordingId) transcriptionWatchdog.disarm(event.detail.recordingId);
      if (!event.detail.error) {
        const targetId = event.detail.chatId;
        // The backend fires this the instant the transcript commits, and we refetch
        // to paint it. But that refetch can beat the write becoming readable (read-
        // replica lag — the same race guarded against in parseRecording's skipRefresh
        // path), returning the chat WITHOUT the new transcript and leaving the open
        // note blank until a manual reload. Long dictations (segmented / drain-
        // assembled recordings) lag the most. So when the transcribed chat is the one
        // currently open, poll getChat until its transcript actually advances, then
        // paint. Bounded + fails open: identical to a single fetch when the chat isn't
        // open or the read isn't lagging.
        const openChat = chat && chat.id === targetId ? chat : null;
        let chatRes;
        if (openChat) {
          const { res } = await refetchChatUntilTranscriptAdvances((id) => api.getChat(id), targetId, openChat);
          chatRes = res;
        } else if (event.detail.recordingId) {
          // Background/native completion: no on-screen baseline, but the
          // parsed-vs-error decision below destroys the native audio copy on
          // "parsed" — so absorb read-replica lag the same way the open-chat
          // path does. With a null baseline the poll returns immediately once
          // the chat shows ANY transcript (an appended chat differs from the
          // empty signal on the first fetch) and only burns its bounded budget
          // when the chat is still blank — exactly the case the guard below
          // must then judge.
          const { res } = await refetchChatUntilTranscriptAdvances((id) => api.getChat(id), targetId, null);
          chatRes = res;
        } else {
          chatRes = await api.getChat(targetId);
        }
        if (!shouldApplyChatDataResponse(requestIdentity, activeChatDataIdentityRef.current)) return;
        updateChatLocal(chatRes.data);
        // Last-line guard before destroying the native audio copy: a "success"
        // completion whose chat holds NO transcript at all means the save never
        // became durable (a run whose DB commit failed AFTER the success
        // notification fired — prod 2026-07-01 marked recordings "parsed" this
        // way and deleted audio for blank notes). Route it down the error path
        // instead: audio retained, retry affordance shown, no note generated
        // from nothing. A false negative here only delays cleanup; a false
        // positive in the old code destroyed the recording.
        if (event.detail.recordingId && !chatHasAnyTranscript(chatRes.data)) {
          console.error("Transcription reported success but the chat has no transcript — treating as failure");
          if (autogenerate.current === targetId) setAutogenerate(false);
          updateChatValue(targetId, { transcribing: false });
          sendToMobileApp({ type: "recordingUpdate", recordingId: event.detail.recordingId, state: "error" });
          if (isMobileApp) dismissRecordingProgressToast(toast, recordingProgressToastId(event.detail.recordingId));
          toast.dismiss(`sync-recording-${event.detail.recordingId}`);
          toast.error(t("hooks.useChats.transcriptionNotSaved"));
          return;
        }
        console.log("Transcription completed");
        if (event.detail.recordingId) {
          sendToMobileApp({ type: "recordingUpdate", recordingId: event.detail.recordingId, state: "parsed" });
          toast.dismiss(`sync-recording-${event.detail.recordingId}`);
        }
        if (isMobileApp && event.detail.recordingId) {
          const toastId = recordingProgressToastId(event.detail.recordingId);
          const message = t("hooks.useChats.transcriptionReady");
          showRecordingProgressToast(toast, {
            toastId,
            message,
            phase: "ready",
            autoDismissMs: 2400,
          });
        } else {
          toast.info(t("hooks.useChats.transcriptionCompleted"));
        }

        if (autogenerate.current === targetId) {
          // The queued "generate after transcription" fired the 2026-07-21
          // incident's boilerplate note: the save committed a header-only
          // transcript and generate ran anyway. Never generate from an
          // effectively-empty transcript — tell the provider instead.
          if (!chatHasMeaningfulTranscript(chatRes.data)) {
            setAutogenerate(false);
            toast.error(t("hooks.useChats.emptyTranscriptCantGenerate"));
          } else {
            // Keep the Generate pill in its spinner-only state from the tap
            // through transcription and generation. Clearing in finally also
            // releases it on plan/generation failures.
            generate(targetId, chatRes.data).finally(() => setAutogenerate(false));
          }
        }
      } else {
        console.error("Error transcribing: " + event.detail.error);
        // The transcription genuinely failed — clear the spinner now. We disarmed
        // the watchdog above (the SSE arrived), so without this the spinner would
        // spin forever: the error branch never painted a chat, and the watchdog's
        // honest-fail ceiling can no longer fire. This is the real-error counterpart
        // to the watchdog's dropped-SSE recovery.
        if (event.detail.chatId) {
          if (autogenerate.current === event.detail.chatId) setAutogenerate(false);
          updateChatValue(event.detail.chatId, { transcribing: false });
        }
        if (event.detail.recordingId) {
          sendToMobileApp({ type: "recordingUpdate", recordingId: event.detail.recordingId, state: "error" });
          if (isMobileApp) dismissRecordingProgressToast(toast, recordingProgressToastId(event.detail.recordingId));
          toast.dismiss(`sync-recording-${event.detail.recordingId}`);
        }

        toast.error(t("hooks.useChats.errorTranscribing", { error: event.detail.error }));
      }
    };

    // Regen aborted (sync API failure or 90s hard-timeout). Restore the
    // section's pre-regen content so the user keeps what they had instead of
    // losing it to a failed regeneration. The red "edit manually" flash is
    // handled by the SectionRegenIndicator component.
    const regenAborted = (event) => {
      const detail = event?.detail;
      if (!detail) return;
      if (chat?.id !== detail.chatId) return;
      if (detail.beforeContent !== undefined) {
        updateContext(detail.variable, detail.beforeContent);
      }
    };

    // SSE-recovery: useJobs publishes this if a regen has been running past
    // a sanity threshold (30s, then again at 60s) without completing. Acts
    // as a one-shot fallback when the SSE was dropped or never sent.
    const regenRecoveryCheck = async (event) => {
      if (!event?.detail?.chatId) return;
      const updatedChat = await refreshChat(event.detail.chatId);
      if (updatedChat?.contextVariables) {
        // Recovery at 30s/60s — by definition something is wrong. Same FIFO
        // best-guess as SSE: mark the oldest pending if no content changed.
        markRegeneratingCompleteIfChanged(event.detail.chatId, updatedChat.contextVariables, {
          markOldestIfNoneChanged: true,
        });
      }
    };

    subscribe("generationCompleted", generationCompleted);
    subscribe("generationRecoveryCheck", generationRecoveryCheck);
    subscribe("transcriptionCompleted", transcriptionCompleted);
    subscribe("importEncounter", importEncounter);
    subscribe("encounterCreated", encounterCreated);
    subscribe("encounterUpdated", encounterUpdated);
    subscribe("reportCardFix", saveReportCard);
    subscribe("regenRecoveryCheck", regenRecoveryCheck);
    subscribe("regenAborted", regenAborted);
    return () => {
      unsubscribe("generationCompleted", generationCompleted);
      unsubscribe("generationRecoveryCheck", generationRecoveryCheck);
      unsubscribe("transcriptionCompleted", transcriptionCompleted);
      unsubscribe("importEncounter", importEncounter);
      unsubscribe("encounterCreated", encounterCreated);
      unsubscribe("encounterUpdated", encounterUpdated);
      unsubscribe("reportCardFix", saveReportCard);
      unsubscribe("regenRecoveryCheck", regenRecoveryCheck);
      unsubscribe("regenAborted", regenAborted);
    };
    // eslint-disable-next-line
  }, [chat, chats, auth?.user?.id, isMobileApp]);

  const loadMore = async () => {
    const requestIdentity = chatDataIdentity;
    setLoadingMore(true);
    const lastScopedChat = getLastServerScopedChat(chats, serverScopedChatIds);
    if (!lastScopedChat) {
      setLoadingMore(false);
      return;
    }
    let to = lastScopedChat.createdOn;

    try {
      let patients = await api.getChats(
        brainId,
        "",
        to,
        location,
        buildChatListRequestFilters(filters, searchTerm, auth?.user?.id)
      );
      if (!shouldApplyChatDataResponse(requestIdentity, activeChatDataIdentityRef.current)) return;
      setLoadingMore(false);

      let array = [...chats, ...patients.data.chats];
      let key = "id";
      const arrayUniqueByKey = [...new Map(array.map((item) => [item[key], item])).values()];
      // Same temp- policy as refreshList. The id dedupe above already stops a
      // page from duplicating a pinned temp- row (a durable twin arrives under
      // its OWN id, not the temp one), so this pass exists to RETIRE a temp row
      // whose durable twin is in the appended page -- otherwise the encounter
      // would be listed twice, once local and once real.
      setChats(mergeUnsyncedTempChats(chats, arrayUniqueByKey, { syncedTempIds: syncedTempChatIdsRef.current }));
      setServerScopedChatIds((currentIds) => {
        const nextIds = new Set(currentIds);
        patients.data.chats.forEach((chat) => nextIds.add(chat.id));
        return nextIds;
      });
      if (chats.length === arrayUniqueByKey.length) {
        setLoadableMore(false);
      }
    } catch (e) {
      if (!shouldApplyChatDataResponse(requestIdentity, activeChatDataIdentityRef.current)) return;
      setLoadingMore(false);
      toast.error(t("hooks.useChats.loadingError"));
    }
  };

  // After a voice-edit AI rewrite completes, diff the pre-voice section against
  // the AI-merged result and persist the delta as a Manual Input context entry.
  // This is what keeps the dictated content alive across a subsequent
  // mass-regenerate (which would otherwise rebuild the section from raw
  // transcript only and silently drop the voice contribution).
  const captureVoiceEditAsHumanInput = (extraData, updatedSection) => {
    try {
      if (!extraData?.variable || typeof updatedSection !== "string") return;
      const summary = diffSectionEdit(extraData.section || "", updatedSection);
      if (!summary) return;
      const sectionLabel = extraData.sectionTitle || extraData.label || extraData.variable;
      appendManualTranscript("transcript", sectionLabel, summary);
    } catch (e) {
      console.warn("Failed to capture voice edit as human input", e);
    }
  };

  const identityRefusal = (chatId, reason, target, recordingId, startedAt) => {
    const patient = target?.user || target?.patient;
    const createdOn = identityTimeMs(target?.createdOn);
    reportIdentityParked({ reason, recordingId, chatId, startedAt: identityTimeMs(startedAt), createdOn });
    return {
      refused: IDENTITY_REFUSED,
      reason,
      chat: {
        id: chatId,
        userId: target?.userId || target?.user?.id || null,
        createdOn,
        patientLabel: patient ? `${patient.firstName || ""} ${patient.lastName || ""}`.trim() : "",
      },
    };
  };

  // Fetches the target encounter ONCE and applies the identity rule. Nothing to
  // judge (no start time, no patient) or an explicit override skips the fetch, and so
  // does a recording captured less than IDENTITY_FRESH_MS ago on this device (a live
  // dictation: recycling needs the trash retention to pass, and both timestamps are
  // the device clock, so skew cannot matter). The server still checks everything.
  // The fetch is bounded (IDENTITY_FETCH_TIMEOUT_MS). A failed or timed-out fetch
  // throws: the caller's existing classification (404 + gone -> orphaned, otherwise
  // transient: audio kept, row retried) applies, and no audio is sent unchecked.
  const checkRecordingIdentity = async (chatId, recordingId, startedAt, identity) => {
    if (identity.overrideIdentity === true) return { refusal: null, target: null };
    if (startedAt == null && !identity.patientUserId && !identity.encounterUuid) return { refusal: null, target: null };
    const startedMs = identityTimeMs(startedAt);
    // A future start time is not fresh, so it is fetched. A skipped capture also drops
    // patientUserId from the upload (skippedFresh): the server must not apply a patient
    // rule the client never ran, or a server-only refusal loses already-deleted audio.
    const sinceStartMs = startedMs == null ? null : Date.now() - startedMs;
    if (sinceStartMs != null && sinceStartMs >= 0 && sinceStartMs < IDENTITY_FRESH_MS) {
      return { refusal: null, target: null, skippedFresh: true };
    }
    const target = (await api.getChat(chatId, { timeout: IDENTITY_FETCH_TIMEOUT_MS }))?.data || {};
    if (identityTimeMs(target.createdOn) == null) {
      console.warn("identity guard: encounter createdOn is missing or unparseable", { chatId, recordingId });
    }
    const verdict = evaluateRecordingIdentity(
      {
        startedAt,
        patientUserId: identity.patientUserId,
        encounterUuid: identity.encounterUuid,
        chatId: identity.sourceChatId || chatId,
        overrideIdentity: identity.overrideIdentity,
      },
      {
        id: chatId,
        userId: target.userId || target.user?.id,
        createdOn: target.createdOn,
        offlineId: offlineIdOf(target),
        encounterUuid: encounterUuidOf(target),
      },
      { nowMs: Date.now(), autoDrain: !identity.retry }
    );
    if (verdict.ok) return { refusal: null, target };
    return { refusal: identityRefusal(chatId, verdict.reason, target, recordingId, startedAt), target };
  };

  // startedAt (optional): real recording-start time (ms epoch). When provided it is
  // forwarded to api.transcript so an offline-drained recording is stamped with WHEN
  // it was recorded, not the sync-clock time. Omitted by callers that don't have it
  // (behaviour then matches before this param existed).
  // forcedPartial (optional, trailing): true ONLY for the cross-device "Recover
  // anyway" path (recovering an INCOMPLETE recording). Forwarded to api.transcript so
  // the backend saves a supersed-able partial and keeps the recording recoverable.
  // Every other caller omits it → default false → unchanged behaviour.
  // identity (optional, trailing): set ONLY by deferred uploads (queue drain, blob
  // replay, cross-device recovery, native handoff) as
  // { deferred: true, patientUserId, encounterUuid, overrideIdentity, sourceChatId, startedAt, retry }.
  // Encounter ids are recycled after hard deletes, so a deferred upload re-checks
  // the target encounter first and resolves an identity-refusal sentinel instead of
  // attaching audio to another patient. Live uploads omit it and are unchanged.
  const parseRecording = async (
    base64,
    chatId,
    phase,
    extraData,
    textTranscription,
    recordingId,
    callback,
    startedAt,
    forcedPartial = false,
    identity = null
  ) => {
    console.log("Parsing audio", phase);
    // isServerAvailable, not isOnline: transcription is a backend round-trip.
    // During an outage the upload is doomed, so take the same "parsed when
    // online" path the no-internet case takes and let the queue drain on
    // serverBackOnline, rather than burning the recording on a failed POST.
    if (!isServerAvailable) {
      toast.info(t("hooks.useChats.recordingParsedWhenOnline"));
      // THROW, don't soft-return: every queue-drain caller treats a resolved
      // parseRecording as "uploaded" and DELETES the queued audio (including the
      // whole segment set). A server outage says nothing about audio integrity,
      // so signal it with a tag the drain/recovery paths recognise as
      // keep-queued-no-strike rather than as a successful upload.
      const unavailable = new Error("Server unavailable — recording kept queued");
      unavailable.serverUnavailable = true;
      throw unavailable;
    }

    if (!auth.user) {
      console.warn("User not authenticated");
    }

    try {
      if (chatId === "quick_start") {
        const availableNoteTypes = noteTypes
          .filter((item) => {
            // Filter allowed note types
            if (item.newEncounter === false) {
              return false;
            }

            if (!noteAllowed(item.value)) {
              return false;
            }

            return true;
          })
          .map((item) => {
            // Format
            return {
              noteId: item.value,
              noteLabel: item.label,
              noteDescription: item.description,
            };
          });

        let newChat = await api.aiQuickStart(base64, availableNoteTypes, textTranscription, location);
        newChat.data.quickStart = true;
        console.log("Got quick start transcription");

        sendToMobileApp({ type: "recordingUpdate", recordingId, state: "parsed" });

        // setShowNewChat(newChat.data);
        setShowNewChat({
          quickStart: true,
          chat: {
            chatId: newChat.data.output.encounterId,
            // Keep the location used to create this quick-start encounter so
            // opening it later cannot replace it with a newer auth location.
            // Only send the key when the auth location has actually resolved —
            // this object is spread over the modal's own computed default, so an
            // unresolved `undefined` would clobber it and leave the field blank.
            ...(location != null ? { locationId: location } : {}),
            userIdQuickstart: newChat.data.output.userId !== "SuperAdministrator" ? newChat.data.output.userId : null,
            userId:
              newChat.data.output.patientNew || newChat.data.output.userId === "SuperAdministrator"
                ? null
                : newChat.data.output.userId,
            contextVariables: {
              "local:encounterType": newChat.data.output.noteType || "",
              // "local:patientIdentification": "turtle",
              // "local:patientContext": "custom context",
              "local:transcript": newChat.data.output.transcript,
              "local:transcriptTotalDuration": newChat.data.output.transcriptTotalDuration,
            },
          },
          patient: {
            firstName: newChat.data.output.patientFirstName,
            lastName: newChat.data.output.patientLastName,
            dob: newChat.data.output.patientDOB,
            // mobilePhone: "1234",
            custom: {
              gender: newChat.data.output.patientGender,
            },
          },
        });
      } else if (phase === "AI Update Note") {
        let newData = await api.aiUpdateNote(chatId, extraData.prompt, extraData.section, base64, textTranscription);

        if (!newData.data.output.updatedSection) {
          throw new Error("No updated section returned");
        }

        if (newData.data.output.updatedSection.title) {
          newData.data.output.updatedSection.callback = callback;
          if (!newData.data.output.updatedSection.values || newData.data.output.updatedSection.values.length === 0) {
            toast.warn(t("hooks.useChats.noOptionsFoundToUpdate"));
          } else {
            // Choreography: voice → bar runs ("thinking") → bar zooms to 100%
            // and flashes green ("Done") → THEN the confirm-codes modal opens.
            // The flash IS the cue that suggestions are ready. We mark the regen
            // entry complete (triggers the 460ms section-regen-flash keyframe)
            // and wait long enough for it to visually finish before the modal
            // pops up over the section.
            const sectionVar = extraData?.variable;
            if (sectionVar) {
              markRegeneratingCompleteIfChanged(chatId, undefined, { forceCompleteVariable: sectionVar });
              await new Promise((resolve) => setTimeout(resolve, 520));
            }
            setShowEditContextVariable(newData.data.output.updatedSection);
          }
        } else if (callback) {
          sendToMobileApp({ type: "recordingUpdate", recordingId, state: "parsed" });
          callback(newData.data.output.updatedSection);
          captureVoiceEditAsHumanInput(extraData, newData.data.output.updatedSection);
        } else {
          sendToMobileApp({ type: "recordingUpdate", recordingId, state: "parsed" });
          const updatedSection = newData.data.output.updatedSection;
          // Persist FIRST (updateContext snapshots the _orig history from the
          // pre-edit value). skipRefresh: we apply the content locally below —
          // updateEncounter's immediate refetch could race the write on a read
          // replica and clobber it with stale content; the encounterUpdated
          // SSE reconciles state afterwards instead. Awaiting also keeps this
          // write strictly ordered before the transcript append below, so the
          // two Note - Update calls can't interleave.
          const persisted = await updateContext(extraData.variable, updatedSection, { skipRefresh: true });
          if (!persisted) {
            // Save failed (non-200, updateEncounter already toasted) or was
            // intercepted by the reassign modal. Do NOT paint the content or
            // flash "Done" — the screen would claim an unsaved edit saved.
            // Abort the overlay so the user gets the red "edit manually" cue.
            if (extraData?.variable) {
              markRegeneratingAborted(chatId, extraData.variable);
            }
            return;
          }
          // Apply to local state immediately — we already hold the new content;
          // showing it must not depend on the refetch round-trip.
          updateContextLocal(extraData.variable, updatedSection);
          // Land the overlay: bar zooms to 100%, flashes "Done", unmounts on the
          // voice-edit timer. refreshChat's content-diff usually catches this
          // too, but the explicit signal covers a raced or missed refetch.
          if (extraData?.variable) {
            markRegeneratingCompleteIfChanged(chatId, undefined, { forceCompleteVariable: extraData.variable });
          }
          captureVoiceEditAsHumanInput(extraData, updatedSection);
        }
      } else if (phase === "Transcript" && callback) {
        let newData = await api.transcriptOnly(base64);
        callback(newData.data.output.text);
      } else {
        const deferred = !!identity?.deferred;
        const guardStartedAt = identity?.startedAt ?? startedAt;
        let identityTarget = null;
        let skippedFresh = false;
        if (deferred) {
          const checked = await checkRecordingIdentity(chatId, recordingId, guardStartedAt, identity);
          if (checked.refusal) return checked.refusal;
          identityTarget = checked.target;
          skippedFresh = !!checked.skippedFresh;
        }
        // Update chat after transcription ended
        updateChatValue(chatId, { transcribing: true });
        console.log("Calling tanscript API");
        const transcriptArgs = [
          base64,
          chatId,
          phase,
          auth.user?.custom?.transcriptLanguage,
          textTranscription,
          recordingId,
          !callback,
          undefined,
          undefined,
          !callback,
          startedAt,
          forcedPartial,
        ];
        if (deferred) {
          if (identity.overrideIdentity === true) reportIdentityOverride({ recordingId, chatId });
          const claimedChatId = identity.sourceChatId || chatId;
          transcriptArgs.push({
            ...(skippedFresh ? {} : { patientUserId: identity.patientUserId }),
            // Sent even for a fresh capture that skipped the client fetch: the UUID is
            // immutable, so (unlike the patient) the server cannot refuse it on a
            // legitimate change the client never judged.
            encounterUuid: identity.encounterUuid || undefined,
            overrideIdentity: identity.overrideIdentity === true,
            // Guard-only start time: the positional startedAt (transcript header) is
            // unchanged and still absent on the native handoff / blob drain.
            recordingStartedAt: guardStartedAt ?? undefined,
            // The offline id this recording was queued under: a claim the server
            // matches exactly against the chat's own offline id.
            offlineId: typeof claimedChatId === "string" && claimedChatId.startsWith("temp-") ? claimedChatId : undefined,
            identityGuard: "1",
          });
        }
        let res = await api.transcript(...transcriptArgs);

        // Only a synchronous answer carries output; a queue drain's async ack does
        // not (the server then signals the refusal through the transcribe notification).
        if (deferred && res?.data?.output?.error === IDENTITY_ERROR_CODE) {
          updateChatValue(chatId, { transcribing: false });
          let target = identityTarget;
          if (!target) {
            try {
              target = (await api.getChat(chatId))?.data || null;
            } catch (_) {
              target = null;
            }
          }
          return identityRefusal(chatId, res.data.output.reason, target, recordingId, guardStartedAt);
        }

        if (callback) {
          callback(res.data);
        } else if (recordingId) {
          // Async path: the backend acked and the transcription runs server-side,
          // signalling completion via the transcriptionCompleted SSE. Arm the
          // watchdog so a dropped/never-sent event doesn't strand the spinner.
          // baseline = the transcript already on this chat, so a recompose that
          // merely re-emits the same text isn't mistaken for "landed".
          // Large recordings get a longer honest-fail ceiling: the server STT
          // budget alone is up to 360s (120s x 3 attempts), so the default
          // 300s ceiling declared failure on long dictations the server went
          // on to finish — a scary toast for a transcription that landed
          // minutes later. 480s covers the STT worst case plus assembly.
          transcriptionWatchdog.arm({
            chatId,
            recordingId,
            startedAt: Date.now(),
            baseline: chatTranscriptSignal(chats.find((c) => c.id === chatId)),
            ceilingMs: base64 && base64.length > TRANSCRIPT_CHUNK_THRESHOLD ? LONG_RECORDING_CEILING_MS : undefined,
            dataIdentity: chatDataIdentity,
          });
        }
      }
    } catch (e) {
      logRecordingSaveFailure(e, recordingId);
      // toast.error("Error parsing recording");
      updateChatValue(chatId, { transcribing: false });
      throw e;
    }
  };

  // startedAt (optional, trailing): forwarded to parseRecording → api.transcript so
  // the saved transcript carries the recording's real start time. Existing callers
  // that omit it are unaffected.
  const parseBlob = async (blob, chatId, phase, extraData, callback, recordingId, startedAt, options = {}) => {
    const toastId = "encode-recording-" + (chatId || Date.now());
    // Background drains (useIndexDb.checkRecordings) re-attempt every queued
    // recording on login; toasting per recording floods the user. In background
    // mode parseBlob stays silent — the drain shows ONE summary toast and the
    // Local Recordings modal is the status surface. Foreground callers omit
    // options, so live recording keeps its progress/success/error toasts.
    const quiet = options && options.background === true;

    return new Promise((resolve, reject) => {
      const audioContext = new AudioContext();
      const fileReader = new FileReader();

      fileReader.onloadend = async () => {
        try {
          const arrayBuffer = fileReader.result;
          console.log("Array buffer of type: " + typeof arrayBuffer);

          // Show progress toast immediately so the user knows processing has started.
          // The rail metadata makes the sticky lifetime and explicit id legible to
          // the native toast rail: today no native session reaches this path (the
          // recorder branches native to the bridge before the web path, and the
          // drain is always quiet), but parseBlob is exported, and without the
          // metadata any future native caller would get this toast silently
          // retimed to 3s mid-encode.
          if (!quiet)
            toast.info(
              t("hooks.useChats.processingRecording"),
              withToastRailMetadata(
                { toastId, progress: 0, autoClose: false },
                { preserveAutoClose: true, explicitToastId: true }
              )
            );

          // Use the Promise form, not the legacy success/error callbacks. The callback
          // form has no clean failure path here: a decode failure rejects decodeAudioData's
          // returned promise (which goes UNHANDLED) AND, with no error callback, never
          // settles parseBlob — that hang holds useIndexDb's `syncing` guard true and
          // permanently freezes the entire resync queue on one undecodable recording.
          // Awaiting gives a single rejection that the catch below turns into a normal
          // retry, so a corrupt blob fails gracefully instead of deadlocking sync.
          const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

          // Close the AudioContext immediately after decoding so its internal
          // PCM copy is released before the Worker allocates its own buffers.
          audioContext.close();

          const MP3Blob = await encodeAudioBufferToMp3(audioBuffer, (progress) => {
            if (!quiet) toast.update(toastId, { progress });
          });
          // Notify other parts of the app (e.g. debug download, analytics)
          publish("mp3Encoded", { dataUrl: MP3Blob, chatId });
          if (!quiet) {
            toast.update(toastId, {
              render: t("hooks.useChats.recordingReady"),
              type: "success",
              progress: undefined,
              autoClose: 2000,
            });
          }
          // Forward the stable recordingId so a retry after a lost ack reuses the same
          // id and the backend can dedupe instead of saving a duplicate transcript.
          // Resolve with parseRecording's outcome: an offline refusal resolves with
          // RECORDING_SAVE_DEFERRED_OFFLINE, and the drain above this must see it —
          // swallowing it here made a refused save indistinguishable from an accepted
          // one, and the drain deletes its queued row on anything that resolves.
          // options.identity: the queue drain's identity-guard context (see parseRecording).
          resolve(
            await (options?.identity
              ? parseRecording(MP3Blob, chatId, phase, extraData, null, recordingId, callback, startedAt, false, options.identity)
              : parseRecording(MP3Blob, chatId, phase, extraData, null, recordingId, callback, startedAt))
          );
        } catch (error) {
          // Covers decode, encode, and upload failures alike. Close defensively: the
          // success path may have already closed (or be closing) the context, and
          // closing a closing/closed context rejects an un-awaited promise.
          try {
            if (audioContext.state !== "closed") await audioContext.close();
          } catch (closeError) {
            // already closing/closed — nothing to do
          }
          // An unreachable server is not a processing failure: parseRecording has
          // already told the clinician the recording will be parsed when back
          // online, and the caller queues the audio. Retract the progress toast
          // rather than turning it red over a contradictory message.
          if (!quiet) {
            if (error?.serverUnavailable) {
              toast.dismiss(toastId);
            } else {
              toast.update(toastId, {
                render: t("hooks.useChats.errorProcessingRecording"),
                type: "error",
                progress: undefined,
                autoClose: 4000,
              });
            }
          }
          reject(error);
        }
      };

      fileReader.readAsArrayBuffer(blob);
    });
  };

  const setAutogenerate = (value, targetChatId = chat?.id || chat?.chatId) => {
    const pendingChatId = value ? targetChatId : undefined;
    autogenerate.current = pendingChatId;
    setAutogeneratePendingChatId(pendingChatId);
  };

  const reassignEncounter = async () => {
    chat.assignedTo = [auth.user.id];
    let chatUpdate = {
      chatId: chat.id,
      assignedTo: auth.user.id,
      chatLastUpdate: new Date().getTime(),
    };
    await api.updateChat(chatUpdate);
    setChat({ ...chat });

    if (showReassignChat.callback) {
      setTimeout(() => {
        showReassignChat.callback();
        setShowReassignChat(false);
      }, 100);
    } else {
      setShowReassignChat(false);
      refreshChat(chat.id);
    }
  };

  const isToReassign = () => {
    return chat.assignedTo && chat.assignedTo[0] !== auth.user.id && auth?.user?.groups?.indexOf("frontdesk") === -1;
  };

  // True only in the narrow window right after selectChat() swaps in a compact
  // list row (isPreviewRow) whose assignedTo is missing -- i.e. we do NOT yet
  // know whether isToReassign() will read as "mine" or "a teammate's" once
  // refreshChat's authoritative fetch lands. Frontdesk is excluded because their
  // assignment doesn't affect the outcome (isToReassign() is always false for
  // them, see above), so there's nothing ambiguous to wait out.
  // Callers that must not act on a not-yet-confirmed assignedTo (the action-bar
  // mic/Generate vs. Edit Encounter decision) should check this FIRST.
  const isAssignmentPending = () => {
    if (auth?.user?.groups?.indexOf("frontdesk") !== -1) return false;
    return Boolean(chat?.isPreviewRow) && !chat?.assignedTo;
  };

  // `autoAssignEnabled` is referenced below before its own declaration in source
  // order; both are `const` in the same function body and this function isn't
  // invoked until render, by which point the module has finished evaluating, so
  // the temporal-dead-zone window is closed. See autoAssignEnabled's own comment
  // for what it means.
  const isEditable = () => {
    return (
      (!isToReassign() || autoAssignEnabled) &&
      chat.status !== "archived" &&
      chat.status !== "invalid" &&
      !chat.isTableEdit &&
      !showReportCard
    );
  };

  // Mobile takeover gate. isMobile is a viewport width (< 950px, see
  // useViewport.js), not a device -- which is why the Chrome extension, whose
  // panel is narrow on a desktop machine, is excluded explicitly. The extension
  // is a deliberate desktop workflow and keeps the Edit Encounter confirm flow.
  // isChromeExtension is a useState() with no initial value, set only inside an
  // effect (useAuth.js) whose try/catch swallows the cross-origin access error
  // the extension's iframe throws -- so it reads undefined both on first render
  // AND in that iframe case. `=== false` requires positive proof we're NOT the
  // extension; a bare `!isChromeExtension` would treat "haven't found out yet"
  // as "safe," opening the gate inside the extension itself.
  const autoAssignEnabled = isMobile && isChromeExtension === false;

  // On mobile, acting on a teammate's encounter assigns it to the actor instead
  // of blocking behind Edit + a confirm modal. Returns whether the caller's
  // write may proceed: false when an assignment attempted against a reachable
  // server failed, or when a compact preview row's real assignment couldn't be
  // confirmed at all -- neither by the server nor by the offline chat cache.
  // Writing to a teammate's chart on a failed or unconfirmed ownership transfer
  // is worse than the friction this replaces.
  //
  // Takes the target chat as an argument and never reads module-scoped `chat`:
  // several updateEncounter callers write to a chat other than the selected one,
  // and deciding off module scope would assign the wrong record.
  //
  // Refusal policy lives HERE, not at the call sites. Callers that gate in place
  // instead of routing through updateEncounter (ModalVitals' offline branch,
  // ModalNewChat's update branch) used to get neither the user-facing signal nor
  // the ICD-10 pending-record cleanup, so a refusal read to the clinician as the
  // app doing nothing at all. Every false return below goes through refuse(),
  // which means calling this is the whole gate -- no caller has to remember a
  // second step.
  //
  // `pendingWrite` is the object this call is gating, and it scopes that
  // cleanup: only a write actually carrying local:billableCodesICD10 may drop a
  // pending ICD-10 record (see icd10PendingSync.onRefused). It defaults to the
  // target chat, which is right for a caller that writes that record whole;
  // updateEncounter passes its delta instead, because the target chat there
  // carries every key on the chart rather than the ones being written.
  const ensureAssignedToMe = async (targetChat, pendingWrite = targetChat) => {
    if (!autoAssignEnabled) return true;
    if (!targetChat) return true;
    // .includes, NOT `indexOf(...) !== -1`: with groups undefined the optional
    // chain yields undefined, and `undefined !== -1` is TRUE — which bypassed the
    // entire gate for anyone whose groups had not loaded yet. That is a live window
    // during recovery, where setServerReachable(true) lands before checkAuth(force)
    // completes and writes run against a groups-less user stub. Fail closed.
    if (auth?.user?.groups?.includes("frontdesk")) return true;

    let target = targetChat;

    // Resolve the chat id ONCE, tolerating both record shapes: loaded chats
    // carry `id`, but plenty of records here are `chatId`-only (every
    // updateEncounter payload, ModalNewChat's modal state). Every id-consuming
    // path below reads this instead of a field, so no caller can defeat the
    // gate -- or its refusal cleanup -- just by handing over the other shape.
    // That silent-skip-by-record-shape is the same failure class this whole
    // consolidation exists to remove.
    const targetId = targetChat.id || targetChat.chatId;

    // Single exit for every refusal. The toast carries a fixed toastId for the
    // same reason the assignment toast below does: a burst of section saves
    // must yield one message, not eight.
    const refuse = () => {
      toast.error(t("hooks.useChats.encounterAssignmentRefused"), { toastId: "encounter-assignment-refused" });
      // The ICD-10 durable pending record is written by the section component
      // BEFORE the write is gated (see ChatNoteSectionBodyICD10's
      // persistIcdChange), and the replay effect re-fires it with
      // skipAssign: true -- so a payload refused here for lack of ownership
      // would land on the teammate's chart on the next mount/reconnect with
      // this gate bypassed entirely. Drop it at the refusal, which is the only
      // place that can tell "refused" apart from "queued while the server was
      // unreachable": a genuine offline/outage edit gets true back from here
      // and never reaches this path.
      icd10PendingSync.onRefused(targetId, pendingWrite?.contextVariables);
      return false;
    };

    // A compact list row (see selectChat) can omit assignedTo. Deciding now
    // would read "unknown" as "mine" and skip the transfer, writing silently
    // into a teammate's chart -- the hole the disabled mic used to cover.
    if (target.isPreviewRow && !target.assignedTo) {
      const resolved = await refreshChat(targetId);
      // refreshChat resolves to undefined when it has no authoritative answer
      // -- the server is unreachable (offline OR a server outage), the request
      // failed, or a stale chat-identity abort fired (see its early-return
      // paths below). Falling back to the original compact row here would read
      // its missing assignedTo as "unknown == mine" and let the write through,
      // exactly the hole this branch exists to close.
      if (resolved) {
        target = resolved;
      } else {
        // Before failing closed, ask the offline chat cache -- but only for a row
        // that actually STATES an owner.
        //
        // CORRECTION (audit #2). An earlier version of this comment claimed a
        // cached row is a whole chat object from a server response, and therefore
        // that a cached row with no assignedTo means "confirmed unassigned". It is
        // not: refreshList caches the web/organization-chats projection, which
        // OMITS assignedTo even for an assigned encounter (api.js documents this,
        // and the separate Get Encounters Assignees process exists precisely to
        // fill it in). So the cached row is the SAME thin projection as the preview
        // row that sent us here -- reading its missing assignedTo as "unassigned"
        // would re-open the unknown-reads-as-mine hole one level down and let an
        // offline edit land silently in a teammate's chart.
        //
        // Therefore: a cached row WITHOUT assignedTo is UNKNOWN -> refuse. Only a
        // row that explicitly names an owner is usable, and then the checks below
        // decide (mine -> proceed; a teammate's -> the stays-assigned offline
        // path). persistChatWindowWithOwnership hydrates the window through
        // api.getEncounterAssignees before saveChatList, so a cached row CAN now
        // carry a true owner and this fallback is reachable rather than dead.
        //
        // Consulted ONLY while the server is unreachable. With the server up,
        // refreshChat resolving undefined means a real failure or an identity
        // abort, and a cache read must not paper over either.
        const cached = isServerAvailable
          ? null
          : await loadChatList(buildScope(auth?.user?.id, auth?.session?.currentOrganizationName));
        // Same both-shapes tolerance as targetId above, so a cached row keyed
        // the other way can't silently read as a cache miss.
        const cachedRow = cached?.rows?.find((row) => (row?.id || row?.chatId) === targetId);
        // Cache miss, or a row that says nothing about ownership: with no
        // confirmed owner from any source, block the write instead of guessing.
        // statesOwner is the hydration's own predicate, so an empty assignedTo
        // is "says nothing" here exactly as it is there.
        if (!cachedRow || !statesOwner(cachedRow)) return refuse();
        // A stale snapshot is not evidence either -- ownership may have moved days
        // ago. Past the window, treat it as unknown and refuse rather than name an
        // owner we cannot stand behind.
        if (!isCacheFresh(cached?.fetchedAt, CACHED_OWNERSHIP_MAX_AGE_MS)) return refuse();
        target = cachedRow;
      }
    }

    if (!target.assignedTo || target.assignedTo[0] === auth.user.id) return true;

    // Read the outgoing owner BEFORE stampLocally() overwrites it -- the audit
    // record below is worthless without "taken from whom", and by the time the
    // transfer has succeeded this field is already the acting user.
    const previousAssignee = Array.isArray(target.assignedTo) ? target.assignedTo[0] : target.assignedTo;

    // Called from the ONLINE success path only. Mutates the caller's record as
    // well as chat state because callers build their write payload from the same
    // object, so the new owner rides along with the edit instead of being
    // dropped -- and here that is honest, because the server has already
    // accepted the transfer by the time this runs. The offline branch
    // deliberately does not stamp; see its comment.
    //
    // The user-facing message is NOT raised here: the two call sites below have
    // to say different things (see the offline branch), and one shared toast
    // inside this helper is how the offline path came to claim a transfer that
    // never happens.
    const stampLocally = () => {
      target.assignedTo = [auth.user.id];
      // Functional updater, not a read of the closed-over `chat`: when target
      // came from the refreshChat resolution above, refreshChat has already
      // installed the fresh authoritative object via its own functional
      // setChat. Reading the render-time `chat` here would be the stale
      // preview row and would clobber that fresh object right back to a
      // contextVariables-less stub with isPreviewRow still true.
      setChat((prev) => (prev?.id === targetId ? { ...prev, assignedTo: [auth.user.id] } : prev));
    };

    // isServerAvailable, NOT isOnline: a server outage with the device's
    // internet perfectly healthy is the same situation as being offline for
    // every purpose this branch cares about -- the assignment PUT below cannot
    // land, so taking the online path meant one doomed request, refuse(), and a
    // clinician's edit dropped with "your change was not saved" (field-reported
    // during an outage drill). The queue-and-tell-the-truth path below is the
    // honest answer to both. Everything the comments here say about "offline"
    // therefore covers an outage too: the offline replay is what carries the
    // edit in either case.
    if (!isServerAvailable) {
      // No stampLocally() here, deliberately. The offline replay strips
      // assignedTo (usePostMessages syncOfflineData), so this branch will never
      // produce an ownership change on the server -- and a local stamp made the
      // UI assert one anyway: it put a colleague's chart into the acting user's
      // "My encounters" filter (ChatList.js, which matches on assignedTo[0]) and
      // showed them as the provider in the table view's Provider column
      // (tableViewData.js), until the next refreshList replaced it. That is the
      // same untruth the message below stopped telling, just rendered instead of
      // toasted.
      //
      // Costs nothing where offline work actually happens: an encounter that is
      // already the acting user's -- or unassigned -- returns true at the
      // already-mine check above, before stampLocally is ever reached, so
      // offline editing of your own chart never executed this line to begin
      // with. What the queued payload carries changes (the teammate's id instead
      // of the acting user's) but not what it DOES, because the replay deletes
      // the key either way.
      //
      // Deliberately NOT "Encounter assigned to you" -- that would be untrue.
      // The offline replay strips assignedTo (usePostMessages syncOfflineData)
      // precisely so a diff queued at 09:00 cannot hand a colleague's chart to
      // whoever was offline when it replays at 17:00, which means no transfer
      // reaches the server on this path, now or later. What IS true is that the
      // edit is saved and will sync, so say that and say who the encounter
      // still belongs to. Same fixed-toastId dedupe as the online message, on
      // its own id so the two can never suppress each other.
      toast.info(t("hooks.useChats.encounterStaysAssignedOffline"), { toastId: "encounter-stays-assigned-offline" });
      return true;
    }

    // The axios response interceptor throws on any non-2xx (see
    // services/api.js), so a real failure surfaces as a rejection, not a
    // resolved non-2xx status -- the try/catch below is the path that
    // actually fires in production. Both a rejection and a non-2xx resolution
    // must return false and never throw: callers do
    // `if (!(await ensureAssignedToMe(chat))) return;`, and an unhandled
    // rejection here would break that gate instead of just skipping the write.
    try {
      const res = await api.updateChat({
        chatId: targetId,
        assignedTo: auth.user.id,
        chatLastUpdate: new Date().getTime(),
      });

      if (res.status !== 200 && res.status !== 204) {
        console.error("Error assigning encounter to current user", res);
        return refuse();
      }
    } catch (e) {
      console.error("Error assigning encounter to current user", e);
      return refuse();
    }

    // Durable audit of the ownership change, emitted only once the server has
    // accepted it. This transfer moves a medical record between clinicians with
    // no confirmation dialog anywhere in the flow, so "who took what from whom,
    // and when" has to survive the session -- logActivity batches to the
    // LogUserActivity backend process and stamps the acting user and org itself
    // (see utils/activityLog.js), so only the chat and the outgoing owner are
    // ours to supply.
    //
    // Deliberately NOT emitted on the offline branch above: since the offline
    // replay strips assignedTo (usePostMessages syncOfflineData), an offline
    // stamp is a local display state that never becomes an ownership change on
    // the server, and logging it as one would make the audit trail claim a
    // transfer that never happened.
    logActivity("encounter.autoAssign", { chatId: targetId, previousAssignee });

    stampLocally();
    // Unchanged copy, and the only path entitled to it: the server has accepted
    // the transfer, so the encounter really is this user's now. Fixed toastId so
    // a burst of section edits yields one toast, not eight (same dedupe the
    // note-updated toast uses below).
    toast.info(t("hooks.useChats.encounterAssignedToYou"), { toastId: "encounter-assigned" });
    return true;
  };

  // `options.surfaceVerdict` (replay only) turns this function's silent
  // `return;`s into a stated verdict. Every behavioral change below sits behind
  // that flag, so the other call sites are byte-identical.
  //
  // Why the offline replay needed this at all: the axios instance sets no
  // `validateStatus`, so EVERY non-2xx is a rejection, never a resolved
  // response. The replay's `res?.status !== 200` check could therefore never see
  // a server verdict — the one class the give-up bound exists for was
  // unreachable — while a bare 500 from the un-wrapped pre-write getChat, which
  // says nothing about the payload, was the ONLY thing that could strike. The
  // population was exactly backwards.
  //
  // The contract, when surfaceVerdict is true: always resolves, never throws,
  // always an object.
  //
  //   { verdict: "ok",            status }  200 | 204
  //   { verdict: "rejected",      status }  the server evaluated the payload and said no
  //   { verdict: "unavailable",   status? } outage/network class; nothing was evaluated
  //   { verdict: "unauthorized",  status }  401 | 403; the session, not the payload
  //   { verdict: "refetchFailed", status? } the PRE-WRITE getChat failed — not a verdict
  //   { verdict: "queuedOffline" }          took the offline branch; re-queued locally
  //   { verdict: "invalid" }                nothing was attempted
  const updateEncounter = async (chatUpdate, showToast = true, currentChat, options = {}) => {
    const surface = options.surfaceVerdict === true;
    // Under surfaceVerdict the replay owns ALL user-facing messaging: it knows
    // how many diffs are in the batch and de-dupes per encounter, which this
    // function cannot.
    const notifyError = (key, toastOptions) => {
      if (surface) return;
      // Arity preserved for the option-less calls: existing tests (and toast's
      // own defaults) distinguish `toast.error(msg)` from `toast.error(msg, undefined)`.
      if (toastOptions) toast.error(t(key), toastOptions);
      else toast.error(t(key));
    };
    // A thrown API failure, classified once. `fallback` is what a failure that
    // is neither outage- nor auth-class means AT THIS POINT in the write: for
    // the pre-write refetch that is "refetchFailed" (nothing about the payload
    // can be inferred from it), for the write itself it is "rejected".
    const classifyThrow = (e, fallback) => {
      const status = statusOf(e);
      if (isServerDownError(e)) return { verdict: "unavailable", status };
      if (isAuthStatus(status)) return { verdict: "unauthorized", status };
      return { verdict: fallback, status };
    };

    if (!currentChat) {
      currentChat = chat;
    }

    if (!chatUpdate) {
      console.error("Chat update is empty");
      notifyError("hooks.useChats.encounterNotSelected");
      return surface ? { verdict: "invalid", reason: "noUpdate" } : undefined;
    }

    if (!chatUpdate.chatId) {
      console.error("Chat ID is missing in update");
      notifyError("hooks.useChats.encounterNotSelected");
      return surface ? { verdict: "invalid", reason: "noChatId" } : undefined;
    }

    // Get chat if different from current
    if (!currentChat || currentChat.id !== chatUpdate.chatId) {
      let res;
      try {
        res = await api.getChat(chatUpdate.chatId);
      } catch (e) {
        // Bug-compatible for the other call sites: this throw has always
        // propagated out of updateEncounter, and the replay's catch used to
        // strike on it. Under surfaceVerdict it becomes a stated, NEVER
        // strikeable verdict — nothing that happens before the payload is sent
        // can produce a "rejected", by construction.
        if (!surface) throw e;
        return { ...classifyThrow(e, "refetchFailed"), phase: "refetch" };
      }
      if (res.status !== 200) {
        console.error("Error fetching chat for update", res);
        notifyError("hooks.useChats.errorUpdatingEncounter", { toastId: "error-updating-encounter" });
        return surface ? { verdict: "refetchFailed", status: res.status, phase: "refetch" } : undefined;
      }
      currentChat = res.data;
    }

    // Placed AFTER the refetch above so currentChat is the authoritative record
    // for chatUpdate.chatId -- with a server-fresh assignedTo when the refetch
    // branch just ran; same-chat callers pass whatever currentChat they already
    // hold. skipAssign is for callers that fire with no user action -- four
    // opt-outs total: two below, one in usePostMessages.js's offline sync loop,
    // and one in ChatNoteSectionBodyICD10's mount-effect profile order seed. An
    // automatic writer must never transfer ownership of a teammate's encounter.
    if (!options.skipAssign) {
      // Second argument is the delta, not currentChat: it scopes the refusal's
      // ICD-10 cleanup to what this write actually carries. currentChat holds
      // every key on the chart, so letting it default here would drop a pending
      // ICD-10 record on a refused HPI save -- work the user is still entitled
      // to sync. Refusal messaging and that cleanup both live inside
      // ensureAssignedToMe now, so aborting is all this branch has to do.
      const mayProceed = await ensureAssignedToMe(currentChat, chatUpdate);
      // Unreachable under surfaceVerdict (the replay always passes skipAssign),
      // but the contract says "always an object", and a refusal is emphatically
      // not a verdict on the payload.
      if (!mayProceed) return surface ? { verdict: "invalid", reason: "assignRefused" } : undefined;
    }

    // Check review status

    if (currentChat.contextVariables?.["local:reviewed"] === true && chatUpdate.contextVariables?.["local:reviewed"] !== true) {
      if (currentChat.contextVariables["local:changesNotReviewed"]) {
        chatUpdate.contextVariables["local:changesNotReviewed"] = currentChat.contextVariables["local:changesNotReviewed"];
      } else {
        chatUpdate.contextVariables["local:changesNotReviewed"] = [];
      }

      for (let variable in chatUpdate) {
        if (variable === "chatId" || variable === "chatLastUpdate") continue;

        if (variable === "contextVariables") {
          for (let variable in chatUpdate.contextVariables) {
            let variableFull = "contextVariables." + variable;

            if (
              // If is part of the table columns
              columnsSmartSheetKeys.findIndex((c) => c.key === variableFull) !== -1 &&
              // If has changed
              chatUpdate.contextVariables[variable] !== currentChat.contextVariables[variable] &&
              // If is not already marked as not reviewed
              currentChat.contextVariables["local:changesNotReviewed"].findIndex((v) => v.variable === variableFull) === -1
            ) {
              chatUpdate.contextVariables["local:changesNotReviewed"].push({
                variable: variableFull,
                updatedAt: new Date().toISOString(),
              });

              if (!currentChat.contextVariables["local:table_" + variable.replace("local:", "") + "_comments"]) {
                chatUpdate.contextVariables["local:table_" + variable.replace("local:", "") + "_comments"] = [];
              } else {
                chatUpdate.contextVariables["local:table_" + variable.replace("local:", "") + "_comments"] =
                  currentChat.contextVariables["local:table_" + variable.replace("local:", "") + "_comments"];
              }
              chatUpdate.contextVariables["local:table_" + variable.replace("local:", "") + "_comments"].push({
                comment: "Has been updated for review",
                creator: auth.user.id,
                date: new Date(),
              });
            }
          }
        } else {
          if (
            columnsSmartSheetKeys.findIndex((c) => c.key === variable) !== -1 &&
            chatUpdate[variable] !== currentChat[variable] &&
            currentChat.contextVariables["local:changesNotReviewed"].findIndex((v) => v.variable === variable) === -1
          ) {
            chatUpdate.contextVariables["local:changesNotReviewed"].push({
              variable: variable,
              updatedAt: new Date().toISOString(),
            });
          }
        }
      }
    }

    if (chatUpdate.contextVariables?.["local:reviewed"] === true) {
      chatUpdate.contextVariables["local:changesNotReviewed"] = [];
    }

    // Update chat
    // isServerAvailable, not isOnline: this is the central encounter write. A
    // server outage with the radio up used to take the online branch, fail the
    // PUT and lose the edit; now it falls through to the offline merge + native
    // queue, which replays on reconnect (usePostMessages syncOfflineData).
    //
    // ...but ONLY the native app has that outbox. On web, the Chrome extension and
    // the mobile browser nothing replays a queued edit — sendToMobileApp is a no-op
    // there, serverBackOnline's drain is isMobileApp-gated, and the recovery refresh
    // overwrites local state — so the offline branch would show "changes stored
    // offline" over an edit that is simply gone on the next load. Off-native we take
    // the write path and FAIL HONESTLY instead of losing the edit silently.
    if (isServerAvailable || !isMobileApp) {
      let res;
      try {
        res = await api.updateChat(chatUpdate);
      } catch (e) {
        // The doomed PUT during an outage lands here. Twenty-plus call sites await
        // updateEncounter, most without a catch, so this must resolve rather than
        // reject. Log message only — an axios error carries the request body (PHI).
        console.error("Error updating chat", (e && e.message) || e);
        // THE verdict the replay could never see: axios rejects every non-2xx,
        // so a 400/409/422/500 from "Note - Update" arrives here, not below.
        if (surface) return classifyThrow(e, "rejected");
        // toastId, for everyone: one diff per reconnect used to stack one toast
        // each, and an ordinary online burst of section saves stacked too.
        toast.error(t("hooks.useChats.errorUpdatingChat"), { toastId: "error-updating-chat" });
        return;
      }
      if (res.status === 200 || res.status === 204) {
        // skipRefresh: callers that apply the new value to local state
        // themselves (voice-edit optimistic apply) opt out of this immediate
        // refetch — a read that races the write can land stale data on top of
        // content the caller already holds. The encounterUpdated SSE from the
        // backend Note - Update notification reconciles state afterwards.
        if (!options.skipRefresh) refreshChat(currentChat.id);
        if (showToast) {
          toast.info(t("hooks.useChats.noteUpdated"), { toastId: "note-updated" });
        }
        return surface ? { verdict: "ok", status: res.status } : res;
      } else {
        // Near-dead: axios resolves only 2xx, so this is reachable for a 2xx
        // that is neither 200 nor 204 (e.g. 201).
        console.error("Error updating chat", res);
        if (surface) return { verdict: "rejected", status: res.status };
        toast.error(t("hooks.useChats.errorUpdatingChat"), { toastId: "error-updating-chat" });
        return;
      }
    } else {
      // Merge contextVariables per key. chatUpdate carries only the edited keys,
      // so spreading it wholesale would blank every OTHER section in the offline
      // local copy (and in the chat sent to the native app) until the next
      // refresh. The server already merges per key when online; mirror that here.
      const mergedOffline = {
        ...currentChat,
        ...chatUpdate,
        contextVariables: { ...currentChat.contextVariables, ...chatUpdate.contextVariables },
      };
      updateChatLocal(mergedOffline);
      // currentChat.assignedTo is whatever the chart already carried -- an
      // ARRAY, which the native queue would replay into a string-typed trigger
      // param. normalizeAssignedToForNative holds that wire contract (shared
      // with ModalVitals' offline branch, the other native enqueue of an
      // encounter); it converts the outgoing copy only.
      //
      // Note the value here is NOT the acting user on a teammate's chart: the
      // offline gate no longer stamps ownership locally, so this carries the
      // real owner. Harmless either way -- the replay deletes assignedTo before
      // it reaches the server (usePostMessages syncOfflineData), so no value in
      // this field can move the chart.
      //
      // The WHOLE-CHAT SNAPSHOT here is REQUIRED, not a hold (audit #2 / B4,
      // now CLOSED). The shell's contract was read directly out of
      // lucasai-app: WebViewPage.js `onChatUpdate` keeps ONE diff row per
      // chatId (a JS-level getDiff-then-insert-or-update upsert; the SQLite
      // table itself has no uniqueness on chatId) and on every subsequent edit
      // recomputes `compareJSON(oldChatFromDiff, event.chat)` -- re-diffing an
      // IMMUTABLE original baseline, captured at the first offline edit,
      // against the incoming payload -- then deepMerges that over the stored
      // diff. It also calls `Db.updateChat(event.chat)` (utils/Db.js), which
      // overwrites the local chats cache wholesale with no merge.
      //
      // So a delta payload ({...chatUpdate, id, chatId}) corrupts BOTH: every
      // field absent from the delta reads as REVERTED against the baseline and
      // lands in the merged diff that replays to the server, and the chats
      // cache -- which seeds `oldChat` for the next encounter's baseline --
      // becomes a partial object. The snapshot is the only payload this design
      // can consume correctly.
      //
      // The delta's actual benefit (not clobbering fields nobody edited, since
      // Note - Update is last-writer-wins and ignores chatLastUpdate) is
      // therefore bought elsewhere: a backend conflict-guard process, in
      // review. Nothing to change here.
      sendToMobileApp({ type: "onChatUpdate", chat: normalizeAssignedToForNative(mergedOffline) });
      toast.info(t("hooks.useChats.changesStoredOffline"));
      // The server never saw this write — it was re-queued locally. Under the
      // old contract this returned the payload, whose missing `status` the
      // replay then had to reason about; now it says so.
      return surface ? { verdict: "queuedOffline" } : chatUpdate;
    }
  };

  // extraVariables: additional `local:` keys written in the SAME api.updateChat
  // payload as `variable` (e.g. a `<phase>_transcript_on` stamp beside a phase
  // transcript). One payload, not a second updateContext call: a Generate or a
  // refetch could land between two writes, and the desktop reassign-confirm
  // detour below would otherwise drop the follow-up write entirely.
  const updateContext = async (variable, value, { currentChat, skipHistory, skipReassign, skipRefresh, extraVariables } = {}) => {
    if (!currentChat) currentChat = chat;

    if (!currentChat) {
      return toast.error(t("hooks.useChats.encounterNotSelected"));
    }

    // On mobile, don't stop for the confirm modal -- fall through to
    // updateEncounter below, which already calls ensureAssignedToMe and
    // transfers ownership before writing. Without this, relaxing isEditable()
    // for autoAssignEnabled newly EXPOSES every section editor (HPI, ICD-10,
    // LOS, Q-codes, Plan of Care, OASIS -- all routed through updateContext)
    // on mobile, but they'd still hit this modal first, which is exactly the
    // friction this task removes. Desktop is untouched: autoAssignEnabled is
    // always false there, so this reduces to the original `isToReassign()` check.
    if (!skipReassign && !autoAssignEnabled && isToReassign()) {
      return setShowReassignChat({
        callback: () => {
          // Re-issue the SAME write the caller asked for. The options used to be
          // dropped here, so a write that carried extraVariables (or skipHistory /
          // skipRefresh) lost them whenever it went through the confirm modal.
          updateContext(variable, value, { currentChat, skipHistory, skipReassign, skipRefresh, extraVariables });
        },
      });
    }

    let chatUpdate = {
      chatId: currentChat.id,
      chatLastUpdate: new Date().getTime(),
      contextVariables: {},
    };

    // Set history variable
    if (!skipHistory && !currentChat.contextVariables["local:" + variable + "_orig"]) {
      chatUpdate.contextVariables["local:" + variable + "_orig"] = chat.contextVariables["local:" + variable];
    }

    // Set new value
    chatUpdate.contextVariables["local:" + variable] = value;

    // Companion keys ride in the same payload (see the extraVariables note above).
    if (extraVariables && typeof extraVariables === "object") {
      for (const [extraName, extraValue] of Object.entries(extraVariables)) {
        chatUpdate.contextVariables["local:" + extraName] = extraValue;
      }
    }

    return updateEncounter(chatUpdate, true, currentChat, { skipRefresh });
  };

  // Org ICD-10 custom-library remap (frontend mirror of the server-side remap).
  // Runs after a generation event loads codes from the server; persists once if any
  // generated code matches the org's library. Idempotent: after remap the source
  // codes are gone, so it does not re-fire. Silent: written via updateEncounter
  // (no _orig history entry, no reassign prompt).
  const remapIcd10IfNeeded = async (targetChat) => {
    try {
      if (!targetChat) return;
      const remapMap = buildIcd10RemapMap(auth?.orgInfo?.icd10Library);
      if (Object.keys(remapMap).length === 0) return;
      const cv = targetChat.contextVariables || {};
      const arr = normalizList(cv["local:billableCodesICD10"]);
      if (!remapApplies(arr, remapMap)) return;

      const contextVariables = { "local:billableCodesICD10": applyIcd10Remap(arr, remapMap) };

      // Keep the optimizer aligned: ModalIC10Optimizer restores alternatives from the
      // pre-validation snapshot `billableCodesICD10Initial`, matching by code. If we
      // remap the working codes but not the snapshot, that lookup misses and the
      // optimizer shows no alternatives for remapped codes — so remap the snapshot too.
      const initialRaw = cv["local:billableCodesICD10Initial"];
      if (initialRaw != null) {
        const initialArr = normalizList(initialRaw);
        if (remapApplies(initialArr, remapMap)) {
          contextVariables["local:billableCodesICD10Initial"] = applyIcd10Remap(initialArr, remapMap);
        }
      }

      // Single persist + default refresh so note display, EHR sync, and the optimizer
      // all read the remapped values from one source of truth.
      // skipAssign: this runs automatically after generation AND from the SSE
      // generationCompleted handler, which fires for any chat in the loaded
      // list -- including a teammate's, generated on their device. Assigning
      // here would transfer ownership with the phone sitting untouched.
      await updateEncounter({ chatId: targetChat.id, chatLastUpdate: new Date().getTime(), contextVariables }, true, targetChat, {
        skipAssign: true,
      });
    } catch (e) {
      console.error("ICD-10 library remap failed", e);
    }
  };

  const updateContextLocal = async (variable, value) => {
    if (!chat) {
      return toast.error(t("hooks.useChats.encounterNotSelected"));
    }

    // Build a NEW chat object. Mutating `chat` in place and calling
    // setChat(chat) passes the SAME reference back to React, which bails out
    // of the state update entirely — the change never reached the screen
    // until some unrelated render happened to pick it up.
    setChat((prev) => {
      if (!prev) return prev;
      const contextVariables = { ...prev.contextVariables };
      if (!contextVariables["local:" + variable + "_orig"]) {
        contextVariables["local:" + variable + "_orig"] = contextVariables["local:" + variable];
      }
      contextVariables["local:" + variable] = value;
      return { ...prev, contextVariables };
    });
  };

  // Mirrors the helper in ChatInputTab so callers outside that component
  // (section editors, voice-edit pipeline) can persist human contributions
  // into the same context-tile stream the backend prompt already consumes.
  // KNOWN LIMIT: `existing` reads the closure chat, so two appends to the
  // SAME variable within one render tick base off the same value and the
  // second silently drops the first (true for the persisted copy even before
  // updateContextLocal went immutable). No current caller does that — keep it
  // that way, or fold the append into a functional setChat updater first.
  const appendManualTranscript = async (variable, label, text, currentChat = chat) => {
    if (!text || !currentChat) return;
    const now = moment().format("MM/DD/YYYY @ HH:mm");
    const infoLabel = `${label ? `${label} ` : ""}Manual Input`;
    const existing = currentChat?.contextVariables?.["local:" + variable] || "";
    const newEntry = `\n[${infoLabel} ${now} by ${auth.user.lastName}, ${auth.user.firstName}] ${text}`;
    const newValue = existing + newEntry;
    await updateContext(variable, newValue, { currentChat });
    await updateContextLocal(variable, newValue);
  };

  // Update chat
  const selectChat = async (chatId) => {
    if (chatId) {
      let chat = chats.find((chat) => chat.id === chatId);
      if (chat) {
        // Compact organization-chats rows can omit assignedTo (see ChatList.js
        // listFilterCandidates comment) even when the encounter IS assigned to
        // someone else. Stamp this substitution so isAssignmentPending() can tell
        // "confirmed unassigned" apart from "haven't heard back from refreshChat
        // yet" -- the fetch below will replace this object with the authoritative
        // record momentarily. isToReassign() itself is untouched; it still reads
        // chat.assignedTo directly, so isEditable() and its ~30 consumers keep
        // their existing (pre-fix) behavior during this same window.
        //
        // MUST run BEFORE refreshChat() is fired: refreshChat's offline branch
        // clears isPreviewRow synchronously (no fetch to await), so if it ran
        // first it would clear a flag that hasn't been stamped yet, and the
        // offline stuck-spinner case (see refreshChat's clearPendingPreview)
        // would never actually resolve. React's setState queue applies both
        // updates in the order they're scheduled, so refreshChat's later
        // functional setChat still sees this stamp even though it fires next.
        setChat({ ...chat, isPreviewRow: true });
      }
      refreshChat(chatId, true);
    } else {
      // No encounter in the URL (e.g. returned to the list, or browser-Back to
      // "/"): keep `chat` in sync with the route by clearing the selection, so
      // ChatDetail and its sticky patient-name header unmount instead of
      // lingering over the list. Previously this was a one-way sync (set only,
      // never cleared), which left a stale patient name pinned on the list.
      setChat();
    }
  };

  const refreshChat = async (chatId, forceOpen, refreshOnTableView) => {
    // selectChat() stamps isPreviewRow on the compact-row substitution so
    // isAssignmentPending() can tell "still waiting on this fetch" apart from
    // "confirmed unassigned." Nothing else clears that flag -- if this fetch
    // never resolves (the server is unreachable, or a failure in the catch
    // below), the action bar would show its pending spinner forever, which is
    // worse than the pre-fix behavior it replaced (the compact row's best-guess
    // mic/Generate or Edit Encounter view). Fall back to treating the compact
    // row as authoritative again, same as before isPreviewRow existed.
    const clearPendingPreview = () => {
      setChat((chat) => (chat?.id === chatId && chat.isPreviewRow ? { ...chat, isPreviewRow: false } : chat));
    };

    // isServerAvailable, not isOnline: this gate guards exactly one thing, the
    // api.getChat call below, so its question is "can I reach the server", not
    // "does this device have a network". During an outage the request is doomed
    // and its only outcome is the catch below, so skip straight to the same
    // clearPendingPreview() the offline branch runs -- identical result, one
    // less failing request added to the outage. Recovery is not affected: the
    // server-down probe in useAuth owns that, not this call site.
    if (isServerAvailable) {
      const requestIdentity = chatDataIdentity;
      let updatedChat;
      try {
        updatedChat = await api.getChat(chatId);
        if (!shouldApplyChatDataResponse(requestIdentity, activeChatDataIdentityRef.current)) return;
        if (typeof updatedChat.data !== "object") {
          console.error("Chat not valid " + chatId);
          console.error(updatedChat.data);
        }

        if (refreshOnTableView) {
          updatedChat.data.isTableEdit = true;
          updatedChat.data.userActions = []; // Force refresh of user actions
        }

        // Single ingestion chokepoint for the ICD-10 sex-incompatibility gate: strip (or
        // mirror-substitute) diagnosis codes that are anatomically impossible for the
        // patient's recorded sex before the chat reaches state. Every downstream consumer
        // (note render, {{ }}/[[ ]] EHR templating, sanitizeCodes magic paste, PDF, audit
        // packet) reads local:billableCodesICD10, so gating it here cleans all of them.
        // Fails open on unknown/Other sex; records removals on ...SexFiltered.
        //
        // MUST run BEFORE the pending-sync reconcile below: the reconcile's baseline was
        // captured from gated chat state, so comparing it against the RAW server value
        // would misread every stale refetch on a gated chart as a "genuine new server
        // value" — clearing the pending record, reverting the user's delete/reorder on
        // screen, and firing a spurious "codes were updated" toast. Gating first puts
        // incoming and baseline in the same (gated) space; the keepLocal value already
        // originates from gated state, so the gate stays the final authority either way.
        applyIcd10SexGate(updatedChat.data);

        // Optimistic ICD-10 reconcile: if this chat has an unacked local
        // billableCodesICD10 edit (a delete/reorder still syncing in the
        // background), this refetch must not overwrite it. Compare by content —
        // a stale re-read keeps the local value; a genuinely newer server value
        // (e.g. an AI regeneration the user triggered) is accepted and surfaced.
        // Runs before setChat / updateChatLocal so both see the reconciled
        // object. See utils/icd10PendingSync.js.
        if (updatedChat?.data?.contextVariables && icd10PendingSync.hasPending(chatId)) {
          const incoming = updatedChat.data.contextVariables[icd10PendingSync.ICD_CONTEXT_KEY];
          const result = icd10PendingSync.reconcileIncoming(chatId, incoming);
          if (result.action === "keepLocal") {
            // The ICD value is a stale echo, so this refetch's POC/problem-list
            // realignment keys are equally stale — restore the whole pending
            // payload, not just the ICD key, or the note shows a transiently
            // mismatched code/assessment order until the write acks.
            Object.assign(updatedChat.data.contextVariables, result.payload || {});
            updatedChat.data.contextVariables[icd10PendingSync.ICD_CONTEXT_KEY] = result.value;
          } else if (result.action === "accept" && result.changed) {
            toast.info(t("hooks.useChats.icd10Updated"), { toastId: "icd10-updated" });
          }
        }

        setChat((chat) => {
          // Compare on `id` — loaded chat objects carry `id` (see selectChat,
          // updateChatLocal); the previous `chat?.chatId` guard never matched,
          // so non-forceOpen refreshes only reached the screen via the
          // updateChatLocal side path below.
          if (forceOpen || chat?.id === chatId) {
            return updatedChat?.data;
          }

          return chat;
        });

        if (updatedChat.status === 200) {
          updateChatLocal(updatedChat.data);
        }

        // If the freshly loaded chat already has noteDesign populated, the prior
        // generation has completed server-side. Clear any stale isGenerating left
        // over in localStorage from a missed SSE notification or a tab close
        // mid-run, so the spinner doesn't get stuck on a chat whose note is ready.
        // Skip this when WE just kicked off a generation in this session — the
        // chat already had noteDesign from a prior run, but the new run is still
        // in flight and clearing here would hide the "Generating note" loader.
        if (updatedChat?.data?.contextVariables?.["local:noteDesign"] && !activeGenerationsRef.current.has(chatId)) {
          setGenerating(chatId, false);
        }

        // Per-section regeneration: only finish the entries whose specific
        // contextVariable actually changed. This keeps parallel regens on
        // different sections independent — Section A's content arriving does
        // NOT force-stop Section B's skeleton if B is still processing.
        // Each entry that actually changed transitions to "completing" and
        // unmounts on its own per-mode timer (500ms regen / 2000ms voice-edit).
        markRegeneratingCompleteIfChanged(chatId, updatedChat?.data?.contextVariables);

        return updatedChat?.data;
      } catch (e) {
        if (!shouldApplyChatDataResponse(requestIdentity, activeChatDataIdentityRef.current)) return;
        console.error("Error refreshing chat with id:", chatId, e);
        clearPendingPreview();
      }

      return updatedChat?.data;
    } else {
      clearPendingPreview();
    }
  };

  // Crash / close / navigate-away recovery: on mount and whenever connectivity
  // returns, re-fire any ICD-10 edits that were persisted but never acked (e.g.
  // the tab closed before the background write completed). Keyed by chatId, so a
  // write for one patient is replayed even after navigating to another.
  // skipRefresh: the value is already authoritative locally; the encounterUpdated
  // SSE reconciles afterward. See utils/icd10PendingSync.js.
  useEffect(() => {
    // isServerAvailable, not isOnline: the replay below is a write. Firing it
    // during an outage just fails every pending record and releases the guard.
    // Keyed off the same flag in the deps, so the replay re-arms when the
    // server comes back, not only when the radio does.
    if (!isServerAvailable || !auth?.user?.id) return;
    const pendings = icd10PendingSync.loadAll();
    for (const { chatId, payload, updatedAt, userId } of pendings) {
      // Never replay another user's pending edit (shared-workstation hazard:
      // user A's unacked write must not fire under user B's session). Records
      // from before userId existed have null and are skipped the same way —
      // safer to drop a replay than to write under the wrong account.
      if (userId !== auth.user.id) continue;
      if (icd10ReplayedRef.current.has(chatId)) continue;
      icd10ReplayedRef.current.add(chatId);
      // Stamp the ORIGINAL edit time, not now(): a replayed write from a tab
      // closed days ago must not outrank edits other devices made since then
      // under the server's last-write-wins merge.
      // Serialized through the same per-chat write chain as live edits, with
      // begin/endWrite bracketing, so a replay firing while the user is already
      // editing the reopened chart can never land out of order behind a newer
      // write (the same last-write-wins resurrection the chain exists to stop).
      icd10PendingSync.beginWrite(chatId);
      icd10PendingSync
        .enqueueWrite(chatId, () =>
          updateEncounter(
            { chatId, chatLastUpdate: updatedAt || new Date().getTime(), contextVariables: payload },
            false,
            undefined,
            {
              skipRefresh: true,
              // skipAssign: replays queued edits on mount/reconnect. Without
              // this, a teammate who legitimately took the encounter over while
              // we were offline gets it silently stolen back by the replay.
              skipAssign: true,
            }
          )
        )
        .then((res) => {
          if (res && (res.status === 200 || res.status === 204)) {
            icd10PendingSync.onAck(chatId, payload[icd10PendingSync.ICD_CONTEXT_KEY]);
          }
        })
        .catch(() => {
          // Replay failed — release the guard so the record actually retries on
          // the next reconnect/mount (it previously only retried after a full
          // page reload, contradicting this effect's purpose).
          icd10ReplayedRef.current.delete(chatId);
        })
        .finally(() => icd10PendingSync.endWrite(chatId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isServerAvailable, auth?.user?.id]);

  const updateChatValue = (chatId, chatValues) => {
    let currentChat = chats.find((c) => c.id === chatId);
    if (currentChat) {
      currentChat = { ...currentChat, ...chatValues };
      updateChatLocal(currentChat);
    }

    return currentChat;
  };

  const updateChatLocal = (newChat) => {
    setChat((chat) => {
      setChats((chats) => {
        let found;
        for (let c in chats) {
          if (newChat.id === chats[c].id) {
            chats[c] = newChat;
            found = true;
          }
        }

        if (!found) {
          chats.push(newChat);
        }
        return [...chats];
      });

      if (chat && chat.id === newChat.id) {
        return newChat;
      } else {
        return chat;
      }
    });
  };

  const addChat = (chat) => {
    setChats((prevChats) => {
      // Remove any chat with the same id before adding, preventing duplicate entries
      const filtered = prevChats.filter((c) => c.id !== chat.id);
      return [chat, ...filtered];
    });
  };

  const removeChat = (chatId) => {
    let newChats = chats.filter((c) => c.id !== chatId);
    setChats(newChats);
    setChat();
  };

  const getImages = () => {
    let images = {
      images: [],
      meds: [],
      labs: [],
      recordings: [],
      records: [],
      wounds: [],
      others: [],
      "order-forms": [],
      "hcfa-forms": [],
      eob: [],
      invoices: [],
      "billing-worksheet": [],
      vob: [],
      "clinical-documentation": [],
      "face-sheet": [],
    };
    for (let varCtx in chat.contextVariables) {
      // A deleted document/wound sets its uploadedFile/woundFile var to null, but
      // the KEY stays in contextVariables — so a bare prefix check still surfaces it
      // as a ghost entry (the #1287 "deleted document keeps showing" symptom).
      // Require a truthy fileName so nulled (deleted) entries are excluded here and
      // after a refresh (the backend persists the key with a null value).
      if (varCtx.indexOf("local:uploadedFile") === 0 && chat.contextVariables[varCtx]) {
        let img = varCtx.split("_");
        if (!images[img[1]]) {
          console.warn("Unknown image type: " + img[1]);
          images[img[1]] = [];
        }

        images[img[1]].push({
          name: chat.contextVariables["local:uploadedTitle_" + img[1] + "_" + img[2]],
          fileName: chat.contextVariables["local:uploadedFile_" + img[1] + "_" + img[2]],
          type: img[1],
          index: parseInt(img[2]),
        });
      } else if (varCtx.indexOf("local:woundFile") === 0 && chat.contextVariables[varCtx]) {
        const woundId = varCtx.replace("local:woundFile", "");
        const woundPost = chat.contextVariables["local:woundPost" + woundId];

        images.wounds.push({
          name: chat.contextVariables["local:woundTitle" + woundId],
          fileName: chat.contextVariables["local:woundFile" + woundId],
          timestamp: chat.contextVariables["local:woundTimestamp" + woundId],
          transcript: chat.contextVariables["local:wound" + woundId + "_transcript"],
          transcriptTimestamp: chat.contextVariables["local:wound" + woundId + "_transcript_on"],
          groupId: chat.contextVariables["local:woundGroupId" + woundId],
          woundType: woundPost === true ? "post" : woundPost === false ? "pre" : undefined,
          type: "wounds",
          index: parseInt(woundId),
        });
      }
    }

    const { groups: woundGroups, standalone: standaloneWounds } = images.wounds.reduce(
      (acc, w) => {
        if (w.groupId) {
          return {
            ...acc,
            groups: { ...acc.groups, [w.groupId]: [...(acc.groups[w.groupId] ?? []), w] },
          };
        } else {
          return { ...acc, standalone: [...acc.standalone, w] };
        }
      },
      { groups: [], standalone: [] }
    );

    const processedWounds = [...standaloneWounds];

    Object.values(woundGroups).forEach((group) => {
      // NOTE: assuming max 2 wounds per group (pre and post)
      const first = group.find((w) => w.woundType === "pre") ?? group[0];
      const second = group.find((w) => w.woundType === "post") ?? group[0];

      const primary = first;

      if (group.length > 1 && second && first.index !== second.index) {
        processedWounds.push({ ...primary, secondary: second });
      } else {
        processedWounds.push(primary);
      }
    });

    images.wounds = processedWounds;

    const sortByIndex = (a, b) => {
      if (a.index < b.index) {
        return -1;
      }
      if (a.index > b.index) {
        return 1;
      }
      return 0;
    };

    images.images.sort(sortByIndex);
    images.meds.sort(sortByIndex);
    images.labs.sort(sortByIndex);
    images.records.sort(sortByIndex);
    images.wounds.sort(sortByIndex);

    return images;
  };

  const deleteTranscriptEntry = async (variable, entryIndex) => {
    if (!chat) {
      return;
    }

    const transcriptKey = "local:" + variable;
    const transcriptText = chat.contextVariables[transcriptKey];

    if (!transcriptText) {
      toast.error(t("chatInput.toast.errorDeletingEntry"));
      return;
    }

    const entries = parseTranscript(transcriptText);

    const entryToDelete = entries.find((e) => e.index === entryIndex);
    if (!entryToDelete) {
      toast.error(t("chatInput.toast.errorDeletingEntry"));
      return;
    }

    const deletedEntriesJson = chat.contextVariables["local:deletedTranscriptEntries"] || "[]";
    let deletedEntries = [];

    if (typeof deletedEntriesJson === "object") {
      if (Array.isArray(deletedEntriesJson)) {
        deletedEntries = deletedEntriesJson;
      } else {
        deletedEntries = [deletedEntriesJson];
      }
    } else {
      try {
        deletedEntries = JSON.parse(deletedEntriesJson);
      } catch (e) {
        console.error("Error parsing deletedTranscriptEntries:", e);
        deletedEntries = [];
      }
    }

    deletedEntries.push({
      variable,
      index: entryIndex,
      entryText: entryToDelete.originalEntry,
      deletedBy: `${auth.user.lastName}, ${auth.user.firstName}`,
      deletedAt: new Date().toISOString(),
    });

    const remainingEntries = entries.filter((e) => e.index !== entryIndex);
    const newTranscript = remainingEntries.map((e) => e.originalEntry).join("\n");

    const chatUpdate = {
      chatId: chat.chatId,
      chatLastUpdate: new Date(),
      contextVariables: {
        "local:deletedTranscriptEntries": deletedEntries,
        [transcriptKey]: newTranscript,
      },
    };

    try {
      const res = await updateEncounter(chatUpdate, false);

      // updateEncounter returns undefined on every abort path -- a refused
      // mobile auto-assign included -- and has already toasted the specific
      // reason. Reading res.status here threw a TypeError the catch below
      // swallowed into a SECOND, vaguer errorDeletingEntry toast stacked on top
      // of the refusal. Nothing was deleted and the user has already been told
      // why, so stop here. Same res?.status hardening as the six sibling
      // updateEncounter call sites (ChatInputTab.rename, restore, ...).
      if (!res) return;

      if (res?.status === 200) {
        toast.success(t("chatInput.toast.entryDeleted"));
      } else {
        toast.error(t("chatInput.toast.errorDeletingEntry"));
      }
    } catch (error) {
      console.error("Error deleting transcript entry:", error);
      toast.error(t("chatInput.toast.errorDeletingEntry"));
    }
  };

  const getTranscripts = () => {
    let transcripts = [];
    let sections = noteTypesMap[chat?.contextVariables["local:encounterType"]]?.sections;

    if (!sections) {
      if (chat.contextVariables["local:transcript"]) {
        const entries = parseTranscript(chat.contextVariables["local:transcript"]);
        transcripts.push({
          title: "",
          entries,
          variable: "transcript",
        });
      }
    } else {
      if (chat.contextVariables["local:transcript"]) {
        const entries = parseTranscript(chat.contextVariables["local:transcript"]);
        transcripts.push({
          title: "",
          entries,
          variable: "transcript",
        });
      }

      for (let section of sections) {
        const sectionVariable = section.variable + "_transcript";
        if (chat.contextVariables["local:" + sectionVariable]) {
          const entries = parseTranscript(chat.contextVariables["local:" + sectionVariable]);
          transcripts.push({
            title: section.label,
            entries,
            index: section.variable,
            variable: sectionVariable,
          });
        }

        for (let i = 1; i < 20; i++) {
          const variable = section.variable + "_" + i + "_transcript";

          if (chat.contextVariables["local:" + variable]) {
            const entries = parseTranscript(chat.contextVariables["local:" + variable]);
            transcripts.push({
              title:
                section.label +
                " " +
                i +
                " on " +
                moment(chat.contextVariables["local:" + section.variable + "_" + i + "_on"]).format("MM-DD-YYYY HH:mm"),
              entries,
              // The group-level mic/regenerate/delete buttons in ChatInputTab operate on
              // erReassessment_* variables only — other multi sections (erProcedure) must
              // not surface them or they would corrupt reassessment data.
              generate: section.variable === "erReassessment",
              index: i,
              variable: variable,
            });
          }
        }
      }
    }

    return transcripts;
  };

  const getEncounterType = () => {
    if (chat && chat.contextVariables) return noteTypesMap[chat.contextVariables["local:encounterType"]] || {};
  };

  const getEncounterSettings = () => {
    return noteTypesMap[chat.contextVariables["local:encounterType"]] || {};
  };

  const hasNewTemplate = () => hasNewTemplateForChat(chat, auth);

  const getNoteTemplate = () => getNoteTemplateForChat(chat, auth);

  // The backend Check Plan refuses a billing-blocked org with a bare failure, so a blocked Generate would read as a
  // generic error. Re-read the plan to say why. Admin sessions skip the pre-flight plan gate (checkUserPlan), so
  // they are the ones who get this far. Best effort: any failure here falls back to the generic message.
  const generationFailureMessage = async () => {
    try {
      const plan = (await api.getUserPlan()).data?.output;
      const blocked = billingBlockText(plan, hasGroup("supervisor"), t);
      if (blocked) return blocked;
    } catch (e) {
      console.warn("Could not re-read the plan after a failed generation", e?.message);
    }
    return t("hooks.useChats.generationError");
  };

  const generate = async (chatId, chat) => {
    const canGenerate = await checkUserPlan();
    if (!canGenerate) return;

    if (!chat) chat = chats.find((chat) => chat.chatId === chatId);

    if (!chat) {
      toast.error(t("hooks.useChats.chatNotFoundForGeneration"));
      return console.error("Chat not found for generation: " + chatId);
    }

    if (!chat.contextVariables["local:encounterType"]) {
      toast.warn(t("hooks.useChats.selectEncounterTypeFirst"));
      setShowModalSelectEncounterType(chat);
      return chat;
    }

    if (!hasEdits(chat)) {
      toast.warn(t("hooks.useChats.recordNoteFirstOrAddDocument"));
      return chat;
    }

    // Get generation mode settings — custom notes use their own settings, standard notes use user defaults
    const encounterType = chat.contextVariables["local:encounterType"];
    const isCustomNote = encounterType && encounterType.startsWith("custom:");

    // For custom notes, look up the config from the user's profile (reliable source of truth)
    let resolvedCustomConfig = null;
    if (isCustomNote) {
      const customId = encounterType.replace("custom:", "");
      // Search user notes, then org notes
      resolvedCustomConfig =
        (auth.user.custom?.customNotes || []).find((n) => n.id === customId) ||
        (auth.orgInfo?.customNotes || []).find((n) => n.id === customId);

      if (!resolvedCustomConfig || !resolvedCustomConfig.sections || resolvedCustomConfig.sections.length === 0) {
        toast.error(t("hooks.useChats.customNoteTemplateNotFound"));
        return chat;
      }
    }

    // Notify start
    setGenerating(chat.id, true);
    activeGenerationsRef.current.add(chat.id);
    toast.info(t("hooks.useChats.generatingNote", { lastName: chat?.user?.lastName, firstName: chat?.user?.firstName }));
    try {
      // Mode settings follow the encounter's ASSIGNED PROVIDER, not whoever clicks
      // Generate: staff accounts rarely carry noteSettings, and stamping theirs
      // forced every backend mode switch to its default branch — e.g. the ROS
      // collapsed to the abbreviated one-liner (Tasks#333). The finder only hits
      // the network when the assignee is someone else; any failure inside it
      // degrades to the clicking user's settings (the historical behavior).
      const assignedId = assignedUserId(chat);
      const needsAssignedLookup = assignedId && assignedId !== auth.user.id;
      const assignedDoctor = needsAssignedLookup ? await findDoctorForSettings(assignedId) : undefined;
      const settingsSource = pickSettingsSource(chat, auth.user, needsAssignedLookup ? () => assignedDoctor : null);
      let modeSettings = buildModeSettings(settingsSource);

      // For custom notes, extract formatting settings from the resolved config
      if (isCustomNote) {
        for (const section of resolvedCustomConfig.sections) {
          if (section.settings) {
            for (const [key, value] of Object.entries(section.settings)) {
              modeSettings["local:" + key] = value;
            }
          }
        }
        // Include the full config as a JSON string so it survives Java/GraalVM serialization
        modeSettings["local:customNoteConfig"] = JSON.stringify(resolvedCustomConfig);
      }

      // For ER encounters, ensure dispoType is set so Note-Hub knows which disposition to generate
      if (encounterType === "er") {
        const activeDispoPhase = DISPOSITION_PHASES.find((p) => chat.contextVariables["local:" + p + "_transcript"]);
        if (activeDispoPhase) {
          modeSettings["local:dispoType"] = DISPO_VARIABLE_TO_LABEL[activeDispoPhase] || "";
        }
      }

      // Fingerprint the transcript this note is generated from, so the UI can later
      // detect when new audio or manual-transcription text has landed and flag the
      // note as stale (see isNoteStale / the Generate-button stale shimmer).
      // noteRelevantTranscriptSignal (NOT chatTranscriptSignal) — it excludes
      // section-edit "Manual Input" entries, which aren't new source material.
      // Written with modeSettings below.
      modeSettings["local:noteGeneratedSignal"] = noteRelevantTranscriptSignal(chat);

      let chatUpdate = {
        chatId: chatId,
        chatLastUpdate: new Date(),
        contextVariables: modeSettings,
      };

      // Update chat and patient. updateEncounter returns undefined on any
      // abort path -- including a failed mobile auto-assign -- and it has
      // already toasted the specific reason, so generation must not proceed
      // against a chart update that never landed (or a teammate's chart we
      // were never granted).
      const updateResult = await updateEncounter(chatUpdate, false);
      if (!updateResult) {
        setGenerating(chat.id, false);
        activeGenerationsRef.current.delete(chat.id);
        return chat;
      }
      await updateEncounterData(chat);

      // Start generation — for custom notes, pass config directly as API parameter
      let generateContextVars = isCustomNote ? { "local:customNoteConfig": JSON.stringify(resolvedCustomConfig) } : null;

      // For ER encounters, also pass dispoType directly as a process parameter
      if (encounterType === "er" && modeSettings["local:dispoType"]) {
        generateContextVars = generateContextVars || {};
        generateContextVars["local:dispoType"] = modeSettings["local:dispoType"];
      }

      let generationResult = await api.generate(chat.id, [], false, generateContextVars);

      if (generationResult.data.output?.error) {
        console.error("Generation error for note " + chat.id + ": " + generationResult.data.output.error);
        setGenerating(chat.id, false);
        activeGenerationsRef.current.delete(chat.id);
        toast.error(await generationFailureMessage());
        return chat;
      }
      const latestChat = await refreshChat(chat.id);
      // Apply the org ICD-10 custom-library remap to the freshly generated codes.
      await remapIcd10IfNeeded(latestChat);

      if (latestChat?.contextVariables?.["local:encounterType"] === "er") {
        const currentTotalRaw = parseInt(latestChat.contextVariables["local:totalReassessments"], 10);
        const currentTotal = Number.isNaN(currentTotalRaw) || currentTotalRaw < 0 ? 0 : currentTotalRaw;
        const highestIndex = getHighestReassessmentIndex(latestChat);

        if (highestIndex > currentTotal) {
          await updateContext("totalReassessments", highestIndex, {
            currentChat: latestChat,
            skipHistory: true,
          });
        }
      }
    } catch (e) {
      console.error("Generation failed:", e);
      setGenerating(chat.id, false);
      activeGenerationsRef.current.delete(chat.id);
      toast.error(await generationFailureMessage());
      return chat;
    }
  };

  const updateEncounterData = async (chat) => {
    let encounterData = await api.computeEncounterData(chat.id);
    if (encounterData?.data?.output?.computePatientProfileUpdates) {
      await api.updatePatientProfile(chat.id, encounterData.data.output.computePatientProfileUpdates);
    }
  };

  const deleteChat = async (id) => {
    const markGone = () => {
      audit("Delete encounter", id);
      removeChat(id);
      setShowModalDelete(false);
      toast.success(t("pages.home.encounterDeleted"));
    };
    try {
      await api.deleteChat(id);
      markGone();
    } catch (error) {
      // A 404 means the encounter is already gone server-side (a stale/cached list
      // entry) — drop it from the list instead of erroring. The status lives on the
      // wrapped error's `.cause` because of the api response interceptor (same
      // reason as deleteSelectedChats above).
      const status = error?.response?.status ?? error?.cause?.response?.status ?? error?.cause?.status;
      if (status === 404) {
        markGone();
        return;
      }
      console.error("Error deleting encounter:", error);
      toast.error(t("pages.home.errorDeletingEncounter"));
    }
  };

  const toggleSelectionMode = () => {
    setSelectionMode((prev) => {
      if (prev) setSelectedChatIds([]);
      return !prev;
    });
  };

  const toggleSelectChat = (chatId) => {
    setSelectedChatIds((prev) => {
      if (prev.includes(chatId)) {
        return prev.filter((id) => id !== chatId);
      }
      return [...prev, chatId];
    });
  };

  const selectAllChats = (chatIds) => {
    setSelectedChatIds((prev) => {
      if (prev.length === chatIds.length) return [];
      return chatIds;
    });
  };

  const deleteSelectedChats = async () => {
    // Per-chat resilience: one chat the server can't find (404 — already deleted
    // or a stale/cached entry, e.g. an encounter that no longer exists server-side)
    // must NOT abort the whole batch. Treat success OR 404 as "gone" (remove from
    // the list); only a genuine failure (non-404) is surfaced and kept selected so
    // the user can retry.
    //
    // The api response interceptor (services/api.js) rewraps every rejection as a
    // plain Error with the original axios error on `.cause`, so the HTTP status
    // lives at `reason.cause.response.status`, NOT `reason.response.status`. Reading
    // the wrong path meant a 404 was never recognized as "gone" — it fell through to
    // `failed`, leaving the row stuck with an error toast. Match the rest of the
    // codebase (useAuth.js / api.js read `e.cause.response.status`).
    const ids = [...selectedChatIds];
    const results = await Promise.allSettled(ids.map((id) => api.deleteChat(id)));
    const failed = [];
    const goneIds = [];
    ids.forEach((id, i) => {
      const r = results[i];
      const status =
        r.status === "rejected"
          ? (r.reason?.response?.status ?? r.reason?.cause?.response?.status ?? r.reason?.cause?.status ?? null)
          : null;
      if (r.status === "fulfilled" || status === 404) {
        audit("Delete encounter", id);
        goneIds.push(id);
      } else {
        failed.push(id);
      }
    });

    if (goneIds.length) {
      setChats((prev) => prev.filter((c) => !goneIds.includes(c.id)));
      setChat();
    }

    if (failed.length === 0) {
      toast.success(t("pages.home.encountersDeleted", { count: goneIds.length }));
      setSelectedChatIds([]);
      setSelectionMode(false);
      setShowModalDelete(false);
    } else {
      console.error("Error deleting some encounters:", failed);
      setSelectedChatIds(failed);
      toast.error(t("pages.home.errorDeletingEncounter"));
    }
  };

  const signAction = async (chatId, pinHashed) => {
    let chat = chats.find((chat) => chat.id === chatId);

    try {
      let data = await api.sign(chat.id, pinHashed, auth.user.firstName + " " + auth.user.lastName);
      if (data.data.output.signedOn) {
        setShowRequestPin(false);
        document.activeElement.blur();
        toast.info(t("hooks.useChats.noteSigned", { lastName: chat?.user?.lastName, firstName: chat?.user?.firstName }));
        refreshChat(chat.id);

        if (followUpOnSign) {
          setFollowUpOnSign(false);
          setShowModalCreateFollowUp(true);
        }
      } else {
        document.activeElement.blur();
        toast.error(data.data.output.error);
      }
    } catch (e) {
      // console.error(e);
      document.activeElement.blur();
      toast.error(t("hooks.useChats.wrongPin"));
    }
  };

  const sign = async (chatId, followUpOnSign) => {
    let chat = chats.find((chat) => chat.id === chatId);
    if (!chat.contextVariables["local:noteDesign"]) {
      return toast.warn(t("hooks.useChats.generateNoteFirst"));
    }

    setFollowUpOnSign(followUpOnSign);
    setShowRequestPin(chatId);
  };

  const hasSectionTranscripts = (chat) => {
    if (!chat?.contextVariables) return false;

    const encounterType = chat.contextVariables["local:encounterType"];
    const sections = noteTypesMap[encounterType]?.sections;

    if (!sections) return false;

    for (let section of sections) {
      if (chat.contextVariables[`local:${section.variable}_transcript`]) {
        return true;
      }

      for (let i = 1; i < 20; i++) {
        if (chat.contextVariables[`local:${section.variable}_${i}_transcript`]) {
          return true;
        }
      }
    }

    return false;
  };

  const hasEdits = (chat) => {
    if (
      !chat.contextVariables["local:transcriptTotalDuration"] &&
      !chat.contextVariables["local:uploadedTot_images"] &&
      !chat.contextVariables["local:uploadedTot_records"] &&
      !chat.contextVariables["local:uploadedTot_labs"] &&
      !chat.contextVariables["local:uploadedTot_meds"] &&
      !chat.contextVariables["local:totalWounds"] &&
      !chat.contextVariables["local:patientContext"] &&
      !chat.contextVariables["local:transcript"] &&
      !hasSectionTranscripts(chat)
    ) {
      return false;
    } else {
      return true;
    }
  };

  const saveReportCard = async (event) => {
    let chatId = event.detail.chatId;
    // let chatUpdate = {
    //   chatId: chatId,
    //   assignedTo: auth.user.id,
    //   chatLastUpdate: new Date().getTime(),
    //   contextVariables: {
    //     [event.detail.data.section]: event.detail.data.text,
    //   },
    // };
    // await api.updateChat(chatUpdate);
    refreshChat(chatId);
    setGenerating(chatId, false);

    toast.success(t("hooks.useChats.reportCardUpdated"));
    setShowReportCard(null);
    setLoading(false);
  };

  const addToInsuranceGrid = async () => {
    if (!chat) {
      return console.error("Cannot add to insurance grid, no chat selected");
    }

    if (chat.contextVariables["local:insuranceGrid"]) {
      return;
    }

    // updateContext routes through updateEncounter, whose built-in gate is the
    // ownership decision for this chart. Its result was neither awaited nor
    // checked, so on a refusal the flag never landed on the chart while the
    // encounter was still added to the insurance grid server-side -- the same
    // fail-open shape as ReportCard.save and ModalReviewUserAction.approveRequest.
    // The two writes have to agree: local:insuranceGrid is what stops this from
    // firing twice, so an add without the flag is also a duplicate waiting to
    // happen on the next click.
    const persisted = await updateContext("insuranceGrid", true);
    if (!persisted) return;

    api.addToInsuranceGrid(chat.id);
  };

  return {
    // State
    chats,
    setChats,
    chatsCachedAt,
    chat,
    setChat,
    filters,
    setFilters,
    loadedChatListScope,
    serverScopedChatIds,
    searchTerm,
    setSearchTerm,
    parsedSearch,
    showModalDelete,
    setShowModalDelete,
    showRequestPin,
    setShowRequestPin,
    showNewChat,
    setShowNewChat,
    hasEdits,
    isToReassign,
    isAssignmentPending,
    ensureAssignedToMe,
    // Exported so ChatDetail/ChatNoteTab render off the SAME flag the gate
    // decides with, instead of each re-deriving it. The `=== false` subtlety
    // (see its declaration above) was hand-synced across three files and is
    // exactly the kind of detail that survives a copy but not the next edit.
    autoAssignEnabled,
    isEditable,
    isNoteEnabled,

    // Utils
    showEditContextVariable,
    setShowEditContextVariable,
    updateEncounter,
    updateContext,
    updateContextLocal,
    appendManualTranscript,
    showModalSelectEncounterType,
    setShowModalSelectEncounterType,
    showReassignChat,
    setShowReassignChat,

    // Loades
    loading,
    setLoading,
    loadingMore,
    loadableMore,

    // Actions
    refreshList,
    loadMore,
    updateChatValue,
    selectChat,
    addChat,
    parseRecording,
    parseBlob,
    refreshChat,
    removeChat,
    getImages,
    getTranscripts,
    deleteTranscriptEntry,
    getEncounterType,
    getEncounterSettings,
    getNoteTemplate,
    setAutogenerate,
    autogeneratePendingChatId,
    generate,
    deleteChat,
    selectionMode,
    toggleSelectionMode,
    selectedChatIds,
    toggleSelectChat,
    selectAllChats,
    deleteSelectedChats,
    signAction,
    sign,
    filterIncomplete,
    setFilterIncomplete,
    filterSuggestion,
    setFilterSuggestion,
    reassignEncounter,
    hasNewTemplate,
    importingChat,
    setImportingChat,
    showReportCard,
    setShowReportCard,
    saveReportCard,
    showModalCreateFollowUp,
    setShowModalCreateFollowUp,
    addToInsuranceGrid,
  };
}

export function ProvideChats({ children }) {
  const values = useChatsProvider();
  return <ChatsContext.Provider value={values}>{children}</ChatsContext.Provider>;
}

export const useChats = () => {
  return useContext(ChatsContext);
};
